import './App.scss';

import WindowControls from './components/WindowControls/WindowControls';

// Load debug utilities in development using centralized dev mode
import { devOnly, IS_DEV } from './utils/devMode';
devOnly(() => import('./utils/debugCleanup'));

// Run one-time IDB migrations (cleanup of legacy databases, format changes)
import('./services/idb/idbMigrations').then(m => m.runIDBMigrations()).catch(() => {});

// Start periodic IDB storage monitoring (background, non-blocking)
import('./services/idb/storageMonitor').then(m => m.startStorageMonitor()).catch(() => {});

// ============================================
// UNIFIED REGISTRY INITIALIZATION
// ============================================

import { entityTypeRegistry } from './services/entityTypeRegistry';
import { toolRegistry } from './tools/registry';
import { initializeAllCommands } from './stores/commandRegistryStore';
import { appRegistry } from './services/appRegistryService';

async function loadBundleCommandsAsync(): Promise<void> {
  try {
    const { default: fetchService } = await import('./services/fetchService');
    const { API_BASE_URL } = await import('./api/config');
    const { registerBundleCommands } = await import('./stores/commandRegistryStore');

    const result = await fetchService(`${API_BASE_URL}/entities?entityType=AppProject&limit=50&trashed=false`);
    const entities: any[] = Array.isArray(result) ? result : (result.entities ?? []);

    const bundles: Array<{ bundleId: string; bundleName: string; tools: any[] }> = [];
    for (const entity of entities) {
      const tools = entity.skeleton?.tools;
      if (!Array.isArray(tools) || tools.length === 0) continue;
      bundles.push({
        bundleId: entity._id,
        bundleName: entity.name,
        tools: tools.filter((t: any) => t.name && t.description && t.handlerUrl),
      });
    }

    if (bundles.length > 0) {
      registerBundleCommands(bundles);
    }
  } catch (err: any) {
    console.warn('[App] Failed to load bundle commands (non-fatal):', err.message);
  }
}

// Initializes registries that require no auth token (entity types, tool manifests, commands).
// Safe to call at any time, including before the user has signed in.
async function initializeLocalRegistries() {
  try {
    await Promise.all([
      Promise.resolve().then(() => {
        entityTypeRegistry.loadEntityTypes();
      }).catch((error: Error) => {
        console.warn('[App] Entity type registry initialization failed (non-fatal):', error);
      }),

      toolRegistry.loadAllTools().catch((error: Error) => {
        console.warn('[App] Tool registry initialization failed (non-fatal):', error);
      }),
    ]);
  } catch (error) {
    console.error('[App] Local registry initialization failed:', error);
  } finally {
    await initializeAllCommands();

    try {
      const { hotReloadService } = await import('./apps/AppBuilderApp/services/hotReloadService');
      hotReloadService.initialize();
    } catch (hotReloadError) {
      console.warn('[App] Hot reload initialization failed (non-fatal):', hotReloadError);
    }
  }
}

// Initializes registries that require a valid auth token (app registry, bundle commands).
// Must only be called after the user is authenticated.
async function initializeAuthRegistries() {
  await appRegistry.initialize().catch((error: Error) => {
    console.warn('[App] App registry initialization failed (non-fatal):', error);
  });
  loadBundleCommandsAsync().catch(() => {});
}

import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SessionExpiredModal from './components/SessionExpiredModal/SessionExpiredModal';
import { useSessionMonitor } from './hooks/useSessionMonitor';
import OSSignupModal from './components/modals/OSSignupModal';
import useUserStore from './stores/userStore';

// Lazy load org payment gate
const OrgPaymentGate = React.lazy(() => import('./components/Billing/OrgPaymentGate'));

// Import dataspace context store for payment gate
import { useDataspaceContextStore } from './stores/dataspaceContextStore';

// Import all apps - let webpack tree-shake unused ones
const LLMChefApp = React.lazy(() => import('apps/LLMChef/LLMChef'));
const ListenApp = React.lazy(() => import('apps/ListenApp/ListenApp'));
const LibraryApp = React.lazy(() => import('apps/LibraryApp/LibraryApp').then(m => ({ default: m.LibraryApp })));
const StoreApp = React.lazy(() => import('apps/StoreApp/StoreApp'));
const QuickLauncherApp = React.lazy(() => import('apps/QuickLauncherApp/QuickLauncherApp'));
const MailApp = React.lazy(() => import('apps/MailApp/MailApp').then(m => ({ default: m.MailApp })));
const MessagingApp = React.lazy(() => import('apps/MessagingApp/MessagingApp').then(m => ({ default: m.MessagingApp })));
const PasswordVaultApp = React.lazy(() => import('apps/PasswordVaultApp/PasswordVaultApp').then(m => ({ default: m.PasswordVaultApp })));
const InboxApp = React.lazy(() => import('apps/InboxApp/InboxApp').then(m => ({ default: m.InboxApp })));

function StandaloneMailAppWrapper() {
  return <MailApp windowId="standalone-mail" spaceId="default" onClose={() => {}} />;
}
function StandaloneMessagingAppWrapper() {
  return <MessagingApp windowId="standalone-messaging" spaceId="default" onClose={() => {}} />;
}

// Lazy load large layout components
const UpgradeModal = React.lazy(() => import('./components/UpgradeModal/UpgradeModal'));
const InviteFriendModalLazy = React.lazy(() => import('./components/InviteFriendModal/InviteFriendModal').then(m => ({ default: m.InviteFriendModal })));
const ToolConsentModal = React.lazy(() => import('./components/ToolConsentModal').then(m => ({ default: m.ToolConsentModal })));
const UserSettingsModal = React.lazy(() => import('./components/Space/SystemMenuButton/UserSettings/UserSettingsModal').then(m => ({ default: m.UserSettingsModal })));
const GUILayout = React.lazy(() => import('./components/GUILayout/GUILayout'));
import { SystemContextProvider } from './components/GUILayout/SystemContext';
const ScrollToTop = React.lazy(() => import('./components/ScrollToTop/ScrollToTop'));
const NoIndexByRoute = React.lazy(() => import('./components/SEO/NoIndexByRoute'));
const StandaloneAppLayout = React.lazy(() => import('./components/Space/layouts/StandaloneAppLayout'));
const ElectronAppLayout = React.lazy(() => import('./components/Space/layouts/ElectronAppLayout'));
const ToastContainer = React.lazy(() => import('./components/Toast/Toast').then(m => ({ default: m.ToastContainer })));
import ToastStack from './components/ToastStack/ToastStack';
const UpdateToast = React.lazy(() => import('./components/UpdateToast/UpdateToast').then(m => ({ default: m.UpdateToast })));
const RosettaBanner = React.lazy(() => import('./components/UpdateToast/RosettaBanner').then(m => ({ default: m.RosettaBanner })));
const ScreenRecordingBanner = React.lazy(() => import('./components/UpdateToast/ScreenRecordingBanner').then(m => ({ default: m.ScreenRecordingBanner })));
import mixpanelService from './services/analytics/mixpanelService';
import ga4Service from './services/analytics/ga4Service';
import posthog from 'posthog-js';
import { analytics, isTrackingEnabled } from './services/analytics/analytics';
import { MixpanelProvider } from './services/analytics/providers/mixpanel';
import { GA4Provider } from './services/analytics/providers/ga4';
import { PostHogProvider } from './services/analytics/providers/posthog';
import { ServerRelayProvider } from './services/analytics/providers/serverRelay';
import { LogProvider } from './services/analytics/providers/log';
import { userTrackingService } from './services/users/userTrackingService';
import { createFileLogger } from './utils/logger';
import { parseJwtPayload } from './utils/jwtUtils';
import { enableNavigationPrevention } from './utils/navigationPrevention';
import { isElectron } from './utils/electron';
import { perf } from './utils/perfInstrumentation';

const startPostHogRecordingForAuthenticatedUser = (): void => {
  const token = localStorage.getItem('id_token') || localStorage.getItem('authToken');
  if (!token) return;

  const payload = parseJwtPayload(token);
  if (!payload?.sub) return;

  posthog.identify(payload.sub);
  const recorder = posthog as typeof posthog & { startSessionRecording?: () => void };
  recorder.startSessionRecording?.();
};

import { ThemeProvider } from './contexts/ThemeContext';
const SelfHostedLicenseGate = React.lazy(() => import('./components/SelfHostedLicenseGate'));
// Lazy load marketing pages for better performance
const NSFWAIWriter = React.lazy(() => import('pages/marketing/products/NSFWAIWriter/NSFWAIWriter'));
// PersonaPage migrated to content-renderer at /personas/[slug].tsx
const GrantWriters = React.lazy(() => import('pages/marketing/products/GrantWriters/GrantWriters'));
const PrivateWorkspace = React.lazy(() => import('pages/marketing/products/PrivateWorkspace/PrivateWorkspace'));
const CreativeFreedom = React.lazy(() => import('pages/marketing/products/CreativeFreedom/CreativeFreedom'));
const FanfictionWriter = React.lazy(() => import('pages/marketing/products/FanfictionWriter/FanfictionWriter'));
// SelfPublishedBook landing page is SSR-rendered by the content-renderer at /addons/self-published-book
const SelfPublishedBookRedirect: React.FC = () => {
  React.useEffect(() => {
    window.location.replace('https://rivereditor.com/addons/self-published-book');
  }, []);
  return null;
};
const BookAddonPurchasePage = React.lazy(() => import('pages/marketing/products/BookAddonPurchase/BookAddonPurchasePage'));
const RFPWriters = React.lazy(() => import('pages/marketing/products/RFPWriters/RFPWriters'));
const AcademicWriters = React.lazy(() => import('pages/marketing/products/AcademicWriters/AcademicWriters'));
const HNWWriters = React.lazy(() => import('pages/marketing/products/HNWWriters/HNWWriters'));
const PromptLibraryApp = React.lazy(() => import('apps/PromptLibraryApp/PromptLibraryApp'));
const SpaceCreationPage = React.lazy(() => import('./pages/SpaceCreationPage'));
const NewSpaceRoute = React.lazy(() => import('./pages/NewSpaceRoute'));
import { useAuth } from './hooks/useAuth';
import { useAuth0 } from '@auth0/auth0-react';
import { captureRefCode, upgradeLandingPageIfDedicated } from './services/utm/utmService';
import { useGlobalCmdKShortcut } from './hooks/useGlobalCmdKShortcut';
import { useAmbientSuggestions } from './hooks/useAmbientSuggestions';
import { useGlobalNewWindowShortcut } from './hooks/useGlobalNewWindowShortcut';
import { useGlobalEntitySearchShortcut } from './hooks/useGlobalEntitySearchShortcut';
import { useGlobalElectronTabShortcuts } from './hooks/useGlobalElectronTabShortcuts';
import { useGlobalSpacePanelShortcuts } from './hooks/useGlobalSpacePanelShortcuts';
import { useDefaultsInitializer } from './hooks/useDefaultsInitializer';
import { useQuickEntryContextPush } from './hooks/useQuickEntryContextPush';
const ContactsApp = React.lazy(() => import('apps/ContactsApp/ContactsApp').then(m => ({ default: m.ContactsApp })));
const ShowcasePage = React.lazy(() => import('./pages/ShowcasePage'));
const ShowcaseDocumentPage = React.lazy(() => import('./pages/ShowcaseDocumentPage'));
const PublicBookViewer = React.lazy(() => import('./pages/PublicBookViewer'));
const GoogleDriveCallbackPage = React.lazy(() => import('./pages/GoogleDriveCallbackPage'));
const GmailOAuthCallback = React.lazy(() => import('./pages/GmailOAuthCallback'));
const PlaidOAuthCallbackPage = React.lazy(() => import('./pages/PlaidOAuthCallbackPage'));
const BroadcastUnsubscribePageLazy = React.lazy(() =>
  import('./pages/BroadcastUnsubscribePage/BroadcastUnsubscribePage').then(m => ({ default: m.BroadcastUnsubscribePage }))
);
const BlogAuthCallback = React.lazy(() => import('./pages/BlogAuthCallback'));
const OnboardingIntroDebugButton = React.lazy(() => import('./components/Debug/OnboardingIntroDebugButton'));
const YjsDebugPanel = React.lazy(() => import('./components/Debug/YjsDebugPanel'));

// Load all components - webpack will tree-shake unused ones
const Home = React.lazy(() => import('components/Home/Home'));
const HomeSpaceWrapper = React.lazy(() => import('./pages/HomeSpaceWrapper'));
const ProtectedRoute = React.lazy(() => import('./components/ProtectedRoute'));
const AdminProtectedRoute = React.lazy(() => import('./components/AdminProtectedRoute'));
// Lazy load for better initial bundle size
const SelfhostedLoginPage = React.lazy(() => import('./pages/SelfhostedLoginPage'));

const SuccessPage = React.lazy(() => import('./pages/SuccessPage'));
const PaymentSuccessPage = React.lazy(() => import('./pages/PaymentSuccessPage'));
const OrgPaymentSuccessPage = React.lazy(() => import('./pages/OrgPaymentSuccessPage'));
const InvitationAcceptPage = React.lazy(() => import('./pages/InvitationAcceptPage'));
// DownloadsPage moved to content-renderer at /downloads. SPA route redirects via CrossRuntimeRedirect.
const NotFound = React.lazy(() => import('./pages/NotFound'));
const CrossRuntimeRedirect = React.lazy(() => import('./components/CrossRuntimeRedirect'));
const SpacePageWrapper = React.lazy(() => import('./pages/SpacePageWrapper'));
const SignupPage = React.lazy(() => import('./pages/SignupPage'));
const LoginPage = React.lazy(() => import('pages/LoginPage'));
const ElectronOnboardingPage = React.lazy(() => import('./pages/ElectronOnboarding'));
const ElectronFirstRunPage = React.lazy(() => import('./pages/ElectronFirstRun/ElectronFirstRun'));
const AgentConversationPage = React.lazy(() => import('./components/Space/AgentChat/AgentChat'));
const AgentsHome = React.lazy(() => import('./pages/AgentsHome/AgentsHome'));
const AutomationsStorePage = React.lazy(() => import('./pages/AutomationsStorePage/AutomationsStorePage'));
const OnboardingCardPreview = React.lazy(() => import('./pages/OnboardingCardPreview/OnboardingCardPreview'));
const ToolCardPreview = React.lazy(() => import('./pages/ToolCardPreview/ToolCardPreview'));
const SelfHostedOnboardingPage = React.lazy(() => import('./pages/SelfHostedOnboarding'));

// Standalone app components - load all apps
const BookkeeperApp = React.lazy(() => import('./apps/BookkeeperApp/BookkeeperApp'));
const DocumentAccessWrapper = React.lazy(() => import('./apps/DocsApp/components/DocumentAccessWrapper'));
const AgentPanelRoot = React.lazy(() => import('./agents/AgentPanelRoot'));
const AgentLayout = React.lazy(() => import('./components/GUILayout/AgentLayout'));
const BankApp = React.lazy(() => import('apps/BankApp/components/BankApp'));
const RiverDriveApp = React.lazy(() => import('apps/RiverDriveApp/RiverDriveApp'));

// Load all apps
const TasksApp = React.lazy(() => import('apps/TasksApp/TasksApp'));
const AutomationsApp = React.lazy(() => import('apps/AutomationsApp/AutomationsApp'));
const ChatApp = React.lazy(() => import('apps/ChatApp/ChatApp'));
const LoginApp = React.lazy(() => import('./LoginApp'));

// Admin layout + route components (all lazy-loaded for code splitting)
const AdminLayout = React.lazy(() => import('./pages/internal/admin/AdminLayout'));
const AdminOverviewRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminOverviewRoute'));
const AdminUsersRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminUsersRoute'));
const AdminEntitiesRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminEntitiesRoute'));
const AdminEntitiesKeyMetricsRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminEntitiesKeyMetricsRoute'));
const AdminEntitiesToolsRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminEntitiesToolsRoute'));
const AdminContentRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminContentRoute'));
const AdminGrowthRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminGrowthRoute'));
const AdminActivityRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminActivityRoute'));
const AdminConversionsRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminConversionsRoute'));
const AdminSignupsByPageRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminSignupsByPageRoute'));
const AdminBroadcastRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminBroadcastRoute'));
const AdminAnalyticsOverviewRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminAnalyticsOverviewRoute'));
const AdminAnalyticsMetricRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminAnalyticsMetricRoute'));
const AdminAnalyticsQAURoute = React.lazy(() => import('./pages/internal/admin/routes/AdminAnalyticsQAURoute'));
const AdminActivationRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminActivationRoute'));
const AdminLLMUsageRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminLLMUsageRoute'));
const AdminChatInputRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminChatInputRoute'));
const AdminIntegrationsRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminIntegrationsRoute'));
const AdminOverviewOnboardingRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminOverviewOnboardingRoute'));
const AdminOverviewSystemRoute = React.lazy(() => import('./pages/internal/admin/routes/AdminOverviewSystemRoute'));
const AdminSetup = React.lazy(() => import('./pages/internal/AdminSetup'));

// Marketing pages - lazy load for better initial bundle size
// RiverDocsLanding moved to content-renderer at /. SPA route redirects via CrossRuntimeRedirect.
const MediaGenApp = React.lazy(() => import('apps/MediaGenApp/MediaGenApp'));
const CollegeEssayLanding = React.lazy(() => import('pages/marketing/college-essay/CollegeEssayLanding'));
const HumanFirst = React.lazy(() => import('./pages/marketing/products/HumanFirst/HumanFirst'));
const ConfigureRiver1Page = React.lazy(() => import('./pages/marketing/products/RiverBox1/ConfigureRiver1Page'));
const HumanizerOnboarding = React.lazy(() => import('./pages/onboardingFlows/HumanizerTool/HumanizerOnboarding'));
const NSFWOnboarding = React.lazy(() => import('./pages/onboardingFlows/NSFWWriters/NSFWOnboarding'));
const BlogCreatorOnboarding = React.lazy(() => import('./pages/onboardingFlows/BlogCreator/BlogCreatorOnboarding'));
const SEOBlogCreatorOnboarding = React.lazy(() => import('./pages/onboardingFlows/SEOBlogCreator/SEOBlogCreatorOnboarding'));
const SEOOptimizerOnboarding = React.lazy(() => import('./pages/onboardingFlows/SEOOptimizer/SEOOptimizerOnboarding'));
const ContentPolisherOnboarding = React.lazy(() => import('./pages/onboardingFlows/ContentPolisher/ContentPolisherOnboarding'));
const ImageInserterOnboarding = React.lazy(() => import('./pages/onboardingFlows/ImageInserter/ImageInserterOnboarding'));
const TemplateOnboarding = React.lazy(() => import('./pages/onboardingFlows/TemplateOnboarding/TemplateOnboarding'));

// Platform Landing & General Onboarding
const RiverSpacesLanding = React.lazy(() => import('./pages/marketing/RiverSpacesLanding/RiverSpacesLanding'));
const RiverOnboarding = React.lazy(() => import('./pages/Onboarding/RiverOnboarding'));

// Product Pages
const ProductsPage = React.lazy(() => import('./pages/marketing/products/ProductsPage'));
const AiPersonalComputer = React.lazy(() => import('./pages/marketing/products/AiPersonalComputer/AiPersonalComputer'));
const RiverConfigFlow = React.lazy(() => import('./pages/RiverConfigFlow/RiverConfigFlow'));
// WorkspacePage moved to content-renderer at /workspace. SPA route redirects via CrossRuntimeRedirect.
const WorkLanding = React.lazy(() => import('./pages/WorkLanding/WorkLanding'));
const ConciergeRiverLanding = React.lazy(() => import('./pages/ConciergeRiverLanding/ConciergeRiverLanding'));
const TwitterAgentLanding = React.lazy(() => import('./pages/TwitterAgentLanding/TwitterAgentLanding'));
const LinkedInAgentLanding = React.lazy(() => import('./pages/LinkedInAgentLanding/LinkedInAgentLanding'));
const LinkedInGrowthLanding = React.lazy(() => import('./pages/LinkedInGrowthLanding/LinkedInGrowthLanding'));
const RedditAgentLanding = React.lazy(() => import('./pages/RedditAgentLanding/RedditAgentLanding'));
const SeoAgentLanding = React.lazy(() => import('./pages/SeoAgentLanding/SeoAgentLanding'));
const ClaimPack = React.lazy(() => import('./pages/ClaimPack/ClaimPack'));
const CustomerSuccessLanding = React.lazy(() => import('./pages/CustomerSuccessLanding/CustomerSuccessLanding'));
const AutoResponderLanding = React.lazy(() => import('./pages/AutoResponderLanding/AutoResponderLanding'));
const CustomerSupportAgentLanding = React.lazy(() => import('./pages/CustomerSupportAgentLanding/CustomerSupportAgentLanding'));
const ChrisLanding = React.lazy(() => import('./pages/clients/ChrisLanding/ChrisLanding'));
const StudioClient = React.lazy(() => import('./pages/clients/StudioClient/StudioClient'));
const DavenportRoadhouseClient = React.lazy(() => import('./pages/clients/DavenportRoadhouseClient/DavenportRoadhouseClient'));

// Per-app landing pages (Apple-style, integrated AI agents focus)
const MailLanding = React.lazy(() => import('./pages/apps/MailLanding/MailLanding'));
const TasksLanding = React.lazy(() => import('./pages/apps/TasksLanding/TasksLanding'));
const DriveLanding = React.lazy(() => import('./pages/apps/DriveLanding/DriveLanding'));
const AutomationsLanding = React.lazy(() => import('./pages/apps/AutomationsLanding/AutomationsLanding'));
const ImageStudioLanding = React.lazy(() => import('./pages/apps/ImageStudioLanding/ImageStudioLanding'));
const VideoStudioLanding = React.lazy(() => import('./pages/apps/VideoStudioLanding/VideoStudioLanding'));
const TranscriptLanding = React.lazy(() => import('./pages/apps/TranscriptLanding/TranscriptLanding'));
const TasksAppPage = React.lazy(() => import('./pages/marketing/products/TasksPage/TasksPage'));
const OSSignupFlow = React.lazy(() => import('./pages/OSSignup/OSSignupFlow'));
const CheckoutPage = React.lazy(() => import('./pages/CheckoutPage'));

// Labs pages (internal experiments — served at /labs/*, noindex)
const AvatarPage  = React.lazy(() => import('./pages/labs/AvatarPage'));
const AvatarPage2 = React.lazy(() => import('./pages/labs/AvatarPage2'));
const AvatarPage3 = React.lazy(() => import('./pages/labs/AvatarPage3'));
const RiverBackgrounds = React.lazy(() => import('./pages/labs/RiverBackgrounds/RiverBackgrounds'));

// Enterprise Pages
const LawyerDocsEditor = React.lazy(() => import('./pages/marketing/enterprise/lawyers/LawyerDocsEditor'));

// Use Case Pages
const PreLawDeadlinePanic = React.lazy(() => import('./pages/marketing/use-cases/PreLawDeadlinePanic'));

// Converter Pages
const PdfToWordConverter = React.lazy(() => import('./pages/marketing/convert/pdf-to-word/PdfToWordConverter'));
const ManuscriptDiscovery = React.lazy(() => import('./pages/marketing/manuscript-feedback/ManuscriptDiscovery'));
const ManuscriptAnalysisProgress = React.lazy(() => import('./pages/marketing/manuscript-feedback/ManuscriptAnalysisProgress'));
const ChatGPTToWordConverter = React.lazy(() => import('./pages/marketing/convert/chatgpt-to-word/ChatGPTToWordConverter'));
const MarkdownToWordConverter = React.lazy(() => import('./pages/marketing/convert/markdown-to-word/MarkdownToWordConverter'));

// Tool Pages
const YouTubeScriptCreator = React.lazy(() => import('./pages/marketing/tools/youtube-script-creator/YouTubeScriptCreator'));
const AIProductDescriptionGenerator = React.lazy(() => import('./pages/marketing/tools/ai-product-description-generator/AIProductDescriptionGenerator'));
const CopyeditorLanding = React.lazy(() => import('./pages/marketing/tools/copyeditor/CopyeditorLanding'));
const EssayReviewLanding = React.lazy(() => import('./pages/marketing/essay-review/EssayReviewLanding'));
const WorkflowLanding = React.lazy(() => import('./components/WorkflowLanding'));
const WorkflowInputPage = React.lazy(() => import('./components/WorkflowInputPage/WorkflowInputPage'));
const BlogLanding = React.lazy(() => import('./components/BlogLanding'));
const SEOBlogsLanding = React.lazy(() => import('./pages/marketing/SEOBlogsLanding'));
const GettingStartedPage = React.lazy(() => import('./pages/marketing/getting-started/GettingStartedPage'));
const GhostwriterLanding = React.lazy(() => import('./pages/marketing/use-cases/ghostwriter/GhostwriterLanding'));
const GrantWriterLanding = React.lazy(() => import('./pages/marketing/use-cases/grant-writer/GrantWriterLanding'));
const MarketersLanding = React.lazy(() => import('./pages/marketing/use-cases/marketers/MarketersLanding'));
// ResearchersLanding migrated to content-renderer at /for/researchers
const BusinessOSLanding = React.lazy(() => import('./pages/marketing/BusinessOSLanding'));
// AgentsLanding moved to content-renderer at /automations. SPA route redirects via CrossRuntimeRedirect.
const StartupAgentsLanding = React.lazy(() => import('./pages/marketing/StartupAgentsLanding/StartupAgentsLanding'));
const StudentWorkflowLanding = React.lazy(() => import('./pages/marketing/use-cases/students/StudentWorkflowLanding'));

// Other Apps - conditional
const IngestWizardApp = React.lazy(() => import('apps/IngestWizard/IngestWizard'));
const NotesMarkdownApp = React.lazy(() => import('apps/NotesMarkdownApp/NotesMarkdownApp').then(m => ({ default: m.NotesMarkdownApp })));
const StandaloneEntityPage = React.lazy(() => import('./pages/StandaloneEntityPage'));
const StandaloneEntityView = React.lazy(() => import('./pages/StandaloneEntityView'));

// Wrapper that reads ?view= from the URL and passes it to ContactsApp
function StandaloneContactsApp() {
  const [searchParams] = useSearchParams();
  const viewParam = searchParams.get('view') || undefined;
  return (
    <ContactsApp
      windowId="standalone-contacts"
      spaceId="default"
      initialView={viewParam as any}
      onClose={() => {}}
    />
  );
}

// Wrapper component with key to force remount when space ID changes
function SpacePageWrapperWithKey() {
  const log = createFileLogger('SPACE_PAGE_WRAPPER_WITH_KEY', { enabled: false });
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  
  log.log('[SpacePageWrapperWithKey] 🔑 Rendering with key:', {
    spaceId: id,
    timestamp: new Date().toISOString()
  });
  
  // ✨ INSTANT NAVIGATION: Add transition class to enable animations
  const isFromHome = (location.state as any)?.isPendingSpace === true;
  const isFromCreationPage = (location.state as any)?.isFromCreationPage === true;
  
  React.useEffect(() => {
    if (isFromHome) {
      // Add class to enable entrance animation
      document.body.classList.add('space-entering-from-home');
      
      // Remove after animation completes
      const timer = setTimeout(() => {
        document.body.classList.remove('space-entering-from-home');
      }, 1500);
      
      return () => {
        clearTimeout(timer);
        document.body.classList.remove('space-entering-from-home');
      };
    } else if (isFromCreationPage) {
      document.body.classList.add('space-entering-from-creation');
      
      // Remove after animation completes
      const timer = setTimeout(() => {
        document.body.classList.remove('space-entering-from-creation');
      }, 500);
      
      return () => {
        clearTimeout(timer);
        document.body.classList.remove('space-entering-from-creation');
      };
    }
  }, [isFromHome, isFromCreationPage]);
  
  
  // Use space ID as key to force remount only when space actually changes
  return <SpacePageWrapper key={id} />;
}

function RootHomeApp() {
  const { docId } = useParams<{ docId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Custom navigation handler that doesn't remount the layout
  const handleDocNavigation = useCallback((newDocId: string) => {
    navigate(`/d/${newDocId}`, { replace: false });
  }, [navigate]);
  
  // Handle close - go back to launcher
  const handleClose = useCallback(() => {
    navigate('/', { replace: false });
  }, [navigate]);
  
  // If we're on /d/:docId route, show the document
  if (location.pathname.startsWith('/d/') && docId) {
    return (
      <Suspense fallback={<Loader message="Loading document..." />}>
        <DocumentAccessWrapper 
          windowId="root-docs" 
          spaceId="default" 
          onClose={handleClose} 
          title="Docs"
        />
      </Suspense>
    );
  }
  
  // Otherwise show QuickLauncher
  return (
    <QuickLauncherApp 
      windowId="root-quicklauncher" 
      spaceId="default" 
      onClose={() => {}} 
      title="Quick Launcher"
      onNavigateToDoc={handleDocNavigation}
    />
  );
}

/**
 * /r/:code — Single affiliate referral entry point.
 * Stores the code in localStorage (first-touch-wins) then redirects:
 *   - Authenticated users → / (app loads with AffiliateOfferBanner)
 *   - Unauthenticated users → /signup (ref code survives in localStorage)
 */
function AffiliateRefRoute() {
  const { code } = useParams<{ code: string }>();
  const { isAuthenticated, isLoading } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (code) {
      captureRefCode(code);
    }
  }, [code]);

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      navigate('/', { replace: true });
    } else {
      navigate('/signup', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  return null;
}

function RootRoute() {
  const location = useLocation();
  const log = createFileLogger('ROOT_ROUTE', { enabled: false });

  const electronEnv = isElectron();
  log.log('[QL_FLOW] 🏁 RootRoute rendered:', { pathname: location.pathname });

  if (electronEnv) {
    return <Navigate to="/login" replace />;
  }

  // Allow document routes to render even without authentication (for public documents)
  if (location.pathname.startsWith('/d/')) {
    log.log('[QL_FLOW] 🏁 RootRoute: Rendering RootHomeApp (doc route)');
    return <RootHomeApp />;
  }

  // `/` is served by content-renderer SSR (CR `pages/index.tsx`). In production
  // CloudFront's `default_cache_behavior` routes the request to the CR ALB
  // before the SPA ever loads — these branches are only hit when a SPA
  // session navigates client-side to `/` (e.g. clicking the logo). We force
  // a full reload so CloudFront can route to CR.
  //
  // Self-hosted (`riversystem.ai`) is a different deploy that does NOT have
  // CloudFront fronting it; the host check above still returns the
  // self-hosted login page directly.
  if (window.location.hostname.includes('riversystem.ai')) {
    log.log('[QL_FLOW] 🏁 RootRoute: Rendering SelfhostedLoginPage (self-hosted host)');
    return <SelfhostedLoginPage />;
  }
  log.log('[QL_FLOW] 🏁 RootRoute: hard-nav to CR homepage');
  return <CrossRuntimeRedirect to="/" />;
}

interface LoaderProps {
  message?: string;
}

// Cubic-bezier wave: each half-period is a single C command with both control
// points at the peak y. This guarantees C1 continuity at every zero-crossing
// (tangent directions match by symmetry), so there are no kinks.
const LOADER_W = 260;
const LOADER_H = 60;
const LOADER_MID = LOADER_H / 2;
const LOADER_AMP = 16;
const LOADER_HALF_WAVE = 40;
const LOADER_COLOR = "#4a89dc";
const LOADER_KX = 0.37;
const LOADER_SCROLL = LOADER_HALF_WAVE * 2;

const LOADER_PATH = (() => {
  const totalW = LOADER_W + LOADER_SCROLL;
  const halves = Math.ceil(totalW / LOADER_HALF_WAVE) + 1;
  let d = `M 0 ${LOADER_MID}`;
  for (let s = 0; s < halves; s++) {
    const sign = s % 2 === 0 ? 1 : -1;
    const x0 = s * LOADER_HALF_WAVE;
    const x3 = x0 + LOADER_HALF_WAVE;
    const cy = LOADER_MID + sign * LOADER_AMP;
    d += ` C ${x0 + LOADER_HALF_WAVE * LOADER_KX} ${cy},`
       + ` ${x0 + LOADER_HALF_WAVE * (1 - LOADER_KX)} ${cy},`
       + ` ${x3} ${LOADER_MID}`;
  }
  return d;
})();

const LOADER_REDUCED_MOTION = typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function Loader({ message }: LoaderProps = {}) {
  const displayMessage = message || 'Loading...';
  return (
    <div className="space-loading">
      <svg
        width={LOADER_W}
        height={LOADER_H}
        viewBox={`0 0 ${LOADER_W} ${LOADER_H}`}
        role="img"
        aria-label="Loading"
        style={{ display: 'block', overflow: 'hidden' }}
      >
        <defs>
          <clipPath id="loaderClip">
            <rect x="0" y="0" width={LOADER_W} height={LOADER_H} />
          </clipPath>
        </defs>
        <g clipPath="url(#loaderClip)">
          <path d={LOADER_PATH} fill="none" stroke={LOADER_COLOR}
            strokeWidth={16} strokeLinecap="round" opacity={0.06}>
            {!LOADER_REDUCED_MOTION && (
              <animateTransform attributeName="transform" type="translate"
                values={`0,0;${-LOADER_SCROLL},0`} dur="2.4s" repeatCount="indefinite" />
            )}
          </path>
          <path d={LOADER_PATH} fill="none" stroke={LOADER_COLOR}
            strokeWidth={8} strokeLinecap="round" opacity={0.18}>
            {!LOADER_REDUCED_MOTION && (
              <animateTransform attributeName="transform" type="translate"
                values={`0,0;${-LOADER_SCROLL},0`} dur="2.4s" repeatCount="indefinite" />
            )}
          </path>
          <path d={LOADER_PATH} fill="none" stroke={LOADER_COLOR}
            strokeWidth={4} strokeLinecap="round">
            {!LOADER_REDUCED_MOTION && (
              <animateTransform attributeName="transform" type="translate"
                values={`0,0;${-LOADER_SCROLL},0`} dur="2.4s" repeatCount="indefinite" />
            )}
          </path>
        </g>
      </svg>
      <div style={{
        marginTop: '16px',
        fontSize: '14px',
        color: '#666',
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        animation: 'fadeIn 0.3s ease-in',
        minHeight: '21px',
      }}>
        {displayMessage}
      </div>
    </div>
  );
}

// ─── Loader Variant: River ───────────────────────────────────────────────────
// Full-viewport-width SVG that morphs between two organic meander shapes.
// Uses the same cubic-bezier half-period construction as Sinuous, but with
// varying per-bend amplitudes and a slow shape-morph instead of a translate.
const RIVER_VW = 1200;
const RIVER_VH = 90;
const RIVER_MID = RIVER_VH / 2;
const RIVER_COLOR = "#4a89dc";
const RIVER_SEGS = 7;
const RIVER_SEG_W = RIVER_VW / RIVER_SEGS;
const RIVER_KX = 0.37;

function buildRiverPath(amps: number[]): string {
  let d = `M 0 ${RIVER_MID}`;
  for (let s = 0; s < RIVER_SEGS; s++) {
    const sign = s % 2 === 0 ? 1 : -1;
    const x0 = s * RIVER_SEG_W;
    const x3 = x0 + RIVER_SEG_W;
    const cy = RIVER_MID + sign * amps[s];
    d += ` C ${x0 + RIVER_SEG_W * RIVER_KX} ${cy},`
       + ` ${x0 + RIVER_SEG_W * (1 - RIVER_KX)} ${cy},`
       + ` ${x3} ${RIVER_MID}`;
  }
  return d;
}

const RIVER_PATH_A = buildRiverPath([34, 28, 38, 24, 36, 30, 32]);
const RIVER_PATH_B = buildRiverPath([26, 36, 24, 34, 22, 38, 28]);
const RIVER_PATH_C = buildRiverPath([38, 22, 32, 36, 28, 26, 36]);

export function LoaderRiver() {
  const morphValues = `${RIVER_PATH_A};${RIVER_PATH_B};${RIVER_PATH_C};${RIVER_PATH_A}`;
  const morphTimes = '0;0.33;0.66;1';
  const morphSplines = '0.45 0 0.55 1; 0.45 0 0.55 1; 0.45 0 0.55 1';
  return (
    <svg
      width="100%"
      viewBox={`0 0 ${RIVER_VW} ${RIVER_VH}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Loading"
      style={{ display: 'block', overflow: 'visible' }}
    >
      {/* wide glow — river body */}
      <path fill="none" stroke={RIVER_COLOR} strokeWidth={18} strokeLinecap="round" opacity={0.06}>
        {!LOADER_REDUCED_MOTION
          ? <animate attributeName="d" values={morphValues} keyTimes={morphTimes}
              keySplines={morphSplines} calcMode="spline" dur="7s" repeatCount="indefinite" />
          : <animate attributeName="d" values={RIVER_PATH_A} dur="1s" repeatCount="indefinite" />
        }
      </path>
      {/* mid glow */}
      <path fill="none" stroke={RIVER_COLOR} strokeWidth={8} strokeLinecap="round" opacity={0.15}>
        {!LOADER_REDUCED_MOTION
          ? <animate attributeName="d" values={morphValues} keyTimes={morphTimes}
              keySplines={morphSplines} calcMode="spline" dur="7s" repeatCount="indefinite" />
          : <animate attributeName="d" values={RIVER_PATH_A} dur="1s" repeatCount="indefinite" />
        }
      </path>
      {/* crisp channel */}
      <path fill="none" stroke={RIVER_COLOR} strokeWidth={2.5} strokeLinecap="round">
        {!LOADER_REDUCED_MOTION
          ? <animate attributeName="d" values={morphValues} keyTimes={morphTimes}
              keySplines={morphSplines} calcMode="spline" dur="7s" repeatCount="indefinite" />
          : <animate attributeName="d" values={RIVER_PATH_A} dur="1s" repeatCount="indefinite" />
        }
      </path>
    </svg>
  );
}

// ─── Dev playground page ─────────────────────────────────────────────────────
function DevLoadingPage() {
  const label: React.CSSProperties = {
    fontSize: '12px', fontWeight: 600, color: '#999',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: '24px',
  };
  const card: React.CSSProperties = {
    background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb',
    padding: '32px 48px', display: 'flex', flexDirection: 'column', alignItems: 'center',
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#f7f8fa',
      fontFamily: 'system-ui, -apple-system, sans-serif', padding: '48px 32px',
    }}>
      <h1 style={{ fontSize: '20px', fontWeight: 600, color: '#111', marginBottom: '8px' }}>
        /dev/loading
      </h1>
      <p style={{ fontSize: '13px', color: '#888', marginBottom: '48px' }}>
        Loader animation playground — dev only
      </p>

      {/* Loader */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '32px', marginBottom: '32px' }}>
        <div style={card}>
          <div style={label}>Loader</div>
          <Loader message="Loading your workspace…" />
        </div>
      </div>

      {/* River — full width */}
      <div style={{ ...card, padding: '32px 0 28px', alignItems: 'stretch' }}>
        <div style={{ ...label, paddingLeft: '32px' }}>River</div>
        <LoaderRiver />
      </div>
    </div>
  );
}

// Single-subscription wrapper so OrgPaymentGate only fires one Zustand subscriber.
function OrgPaymentGateWrapper() {
  const paymentGate = useDataspaceContextStore(state => state.paymentGate);
  return (
    <OrgPaymentGate
      isOpen={paymentGate.isOpen}
      orgId={paymentGate.orgId || ''}
      orgName={paymentGate.orgName || ''}
      seatCount={paymentGate.seatCount}
      onPaymentStarted={() => useDataspaceContextStore.getState().hidePaymentGate()}
      onCancel={() => {
        useDataspaceContextStore.getState().hidePaymentGate();
        useDataspaceContextStore.getState().switchToPersonal();
      }}
    />
  );
}

export function App() {
  // Contextual AI modal — opened programmatically by Ambient Intelligence Surface
  const { GlobalCmdKModal, openWithAmbientMessage } = useGlobalCmdKShortcut();

  // Ambient Intelligence Surface — reacts to world events and surfaces suggestions
  const { AmbientBubble } = useAmbientSuggestions({ onAct: openWithAmbientMessage });

  // ✅ Enable Electron tab/navigation shortcuts (Cmd+T, Cmd+W, Cmd+[, Cmd+], Ctrl+Tab)
  useGlobalElectronTabShortcuts();

  // ✅ Enable global shortcuts: Cmd+\ sidebar, Cmd+J chat, Cmd+H home, Cmd+D drive, Cmd+N new thread, Cmd+Enter new window, Cmd+K search
  useGlobalSpacePanelShortcuts();
  useGlobalNewWindowShortcut();
  const { GlobalEntitySearchModal } = useGlobalEntitySearchShortcut();
  
  // ✅ Initialize default apps and tools for user
  useDefaultsInitializer();

  // Push current Space context to Electron main process for Quick Entry (Ctrl+Space)
  useQuickEntryContextPush();

  // Listen for navigation commands from the Quick Entry window via main process IPC
  const navigate = useNavigate();
  React.useEffect(() => {
    if (!isElectron()) return;
    const handler = (_event: unknown, { path }: { path: string }) => {
      navigate(path);
    };
    (window as any).electron?.ipcRenderer?.on('app:navigate', handler);
    return () => {
      (window as any).electron?.ipcRenderer?.removeListener('app:navigate', handler);
    };
  }, [navigate]);

  // Reload this tab when another tab switches accounts, preventing 403 cascades
  // from the wrong token being used mid-session.
  React.useEffect(() => {
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('river_account_switch');
      bc.onmessage = () => window.location.reload();
    } catch {
      // BroadcastChannel not available — fall back to storage event detection
      const handleStorage = (e: StorageEvent) => {
        if (e.key !== 'authToken' || !e.newValue || !e.oldValue || e.newValue === e.oldValue) return;
        window.location.reload();
      };
      window.addEventListener('storage', handleStorage);
      return () => window.removeEventListener('storage', handleStorage);
    }
    return () => { bc?.close(); };
  }, []);

  // Global navigation bridge for non-React callers (command handlers, stores).
  // See gui/src/utils/navigation.ts — any module can dispatch `river:navigate`
  // and this listener forwards it to react-router so history stays clean.
  React.useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ path: string; state?: Record<string, unknown>; replace?: boolean }>).detail;
      if (!detail?.path) return;
      navigate(detail.path, { state: detail.state, replace: detail.replace });
    };
    window.addEventListener('river:navigate', handler);
    return () => window.removeEventListener('river:navigate', handler);
  }, [navigate]);

  const location = useLocation(); // still needed for admin-route check on mount
  const log = createFileLogger('APP', { enabled: false });
  const { user } = useAuth();

  // Upgrade the stored landing page from "/" to the first dedicated path the
  // user visits, so signup attribution prefers the most specific landing page
  // they ever touched. No-op once a dedicated path is already stored.
  React.useEffect(() => {
    upgradeLandingPageIfDedicated(location.pathname);
  }, [location.pathname]);

  // Guard: run systemStore init (orgs + context + globalEntityStore bootstrap)
  // at most once per authenticated user. Navigation changes must NOT re-trigger it.
  const systemStoreInitialized = React.useRef(false);
  
  // Settings modal state for URL deep linking (when on pages without SystemMenuButton)
  const [settingsModalOpen, setSettingsModalOpen] = React.useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = React.useState<string | undefined>();
  const [settingsInitialSection, setSettingsInitialSection] = React.useState<string | undefined>();
  
  // ✨ Initialize promo on first login
  const promoInitialized = React.useRef(false);
  
  React.useEffect(() => {
    if (user && !promoInitialized.current) {
      promoInitialized.current = true;
      import('./stores/promoStore').then(({ default: usePromoStore }) => {
        usePromoStore.getState().markFirstShown();
      }).catch(() => {});
    }
  }, [user]);
  
  // Invite-a-friend modal — triggered from userStore when free user hits 100 credits
  const showInviteFriendModal = useUserStore(state => state.showInviteFriendModal);
  const setShowInviteFriendModal = useUserStore(state => state.setShowInviteFriendModal);
  // Session monitoring for token expiration
  const { isSessionExpiredModalOpen, handleLogin } = useSessionMonitor();

  // Initialize user tracking service on mount. Must run before any analytics calls.
  // Called exactly once; the analytics effect below does NOT repeat this call.
  React.useEffect(() => {
    perf.increment('userTrackingInitCount');
    userTrackingService.init();

    // Skip token-based identification for OAuth callback popups — they share
    // localStorage with the main window and must not modify it.
    const isOAuthCallback = window.location.pathname.includes('/integrations/') && window.location.pathname.includes('/callback');

    log.log('[App] 🔍 CHECKING OAUTH CALLBACK:', {
      pathname: window.location.pathname,
      isOAuthCallback,
    });

    if (isOAuthCallback) {
      log.log('[App] ✅ SKIPPING TOKEN VALIDATION FOR OAUTH CALLBACK PAGE');
      return;
    }

    const idToken = localStorage.getItem('id_token');
    const accessToken = localStorage.getItem('authToken');
    const tokenToUse = idToken || accessToken;

    if (tokenToUse) {
      try {
        const payload = parseJwtPayload(tokenToUse);

        if (!payload) {
          throw new Error('Invalid JWT structure or encoding');
        }

        if (payload.sub) {
          userTrackingService.setUser(payload.sub);

          const hasValidEmail = payload.email && payload.email.trim() !== '';
          const hasValidName = payload.name && payload.name.trim() !== '';

          if (hasValidEmail || hasValidName) {
            userTrackingService.updateUserInfo({
              email: hasValidEmail ? payload.email : undefined,
              name: hasValidName ? payload.name : undefined,
              userId: payload.sub,
            });
          }
        } else {
          throw new Error('Invalid token payload - missing sub field');
        }
      } catch (error) {
        log.error('Error parsing existing auth token for analytics:', error);
        log.warn('[App] Token parsing failed but keeping token - LoginGUI will validate');
      }
    }
  }, []);

  // Global billing polling loop — deferred to idle time since it's not needed before UI is interactive.
  React.useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    let unsubscribe: (() => void) | null = null;
    let lastPlan: any = null;
    let currentInterval: number = 60000;
    let idleHandle: number | ReturnType<typeof setTimeout> | null = null;
    
    const initBillingPolling = async () => {
      const useUserStore = (await import('./stores/userStore')).default;
      const { isFreePlan } = await import('./utils/planUtils');
      
      // Helper to calculate credits remaining
      const getCreditsRemaining = (state: any) => {
        const { currentBill } = state;
        if (!currentBill) return null;
        const usedCents = currentBill.charges?.usedCost || 0;
        const maxCents = currentBill.charges?.monthlyAllowance || currentBill.plan?.monthlyFreeCredits || 50;
        return Math.max(0, maxCents - usedCents);
      };
      
      // Polling interval: free/basic check frequently (overage detection + budget exhaustion).
      // Paid plans (payg/standard/premium) are primarily updated via YJS billing_refresh after each
      // chat stream — this poll is just a background safety net at a lower frequency.
      const getPollingInterval = (plan: string | null, creditsRemaining: number | null) => {
        const isPaidPlan = plan && plan !== 'free' && plan !== 'basic';
        if (isPaidPlan) return 300000; // 5 minutes — safety net only
        if (creditsRemaining !== null && creditsRemaining < 50) {
          log.info('[BILLING_POLLING] Low credits detected, polling every 30s');
          return 30000;
        }
        return 60000;
      };
      
      // Helper to start/restart polling interval
      const startPolling = () => {
        const state = useUserStore.getState();
        const planName = state.plan?.plan ?? null;
        const creditsRemaining = getCreditsRemaining(state);
        const newInterval = getPollingInterval(planName, creditsRemaining);
        
        // Clear existing interval if interval duration changed
        if (intervalId && currentInterval !== newInterval) {
          log.info('[BILLING_POLLING] Interval duration changed, restarting', {
            old: `${currentInterval}ms`,
            new: `${newInterval}ms`
          });
          clearInterval(intervalId);
          intervalId = null;
        }
        
        // Create new interval if needed
        if (!intervalId) {
          currentInterval = newInterval;
          intervalId = setInterval(() => {
            const currentState = useUserStore.getState();
            if (currentState.plan) {
              currentState.fetchCurrentBill();
              
              // After fetch, check if we need to adjust interval (credit level may have dropped)
              const newCreditsRemaining = getCreditsRemaining(currentState);
              const requiredInterval = getPollingInterval(currentState.plan.plan, newCreditsRemaining);
              if (requiredInterval !== currentInterval) {
                startPolling();
              }
            } else {
              if (intervalId) clearInterval(intervalId);
              intervalId = null;
            }
          }, newInterval);
        }
      };
      
      // Subscribe to store changes and check if plan changed
      unsubscribe = useUserStore.subscribe((state) => {
        const { plan, fetchCurrentBill } = state;
        
        // Only react if plan actually changed
        if (plan !== lastPlan) {
          lastPlan = plan;
          
          // Clear existing interval if any
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          
          if (plan) {
            log.info(`[BILLING_POLLING] ${plan.plan} plan detected - starting billing poll`);
            fetchCurrentBill();
            startPolling();
          }
        }
      });
      
      // Trigger initial check with current plan state
      const { plan, fetchCurrentBill } = useUserStore.getState();
      lastPlan = plan;
      if (plan) {
        log.info(`[BILLING_POLLING] Initial ${plan.plan} plan detected - fetching bill`);
        fetchCurrentBill();
        startPolling();
      }
    };
    
    if ('requestIdleCallback' in window) {
      idleHandle = requestIdleCallback(() => initBillingPolling(), { timeout: 5000 });
    } else {
      idleHandle = setTimeout(() => initBillingPolling(), 3000);
    }
    
    return () => {
      if (idleHandle != null) {
        if ('cancelIdleCallback' in window) cancelIdleCallback(idleHandle as number);
        else clearTimeout(idleHandle as ReturnType<typeof setTimeout>);
      }
      if (intervalId) clearInterval(intervalId);
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // When the tab regains focus, refresh HTTP-backed stores that may have drifted
  // while the tab was backgrounded. WebSocket reconnection is handled entirely by
  // wsReconnectCoordinator (registered via wsTokenRefresher) — calling provider.connect()
  // here would bypass its stagger logic and cause a thundering-herd reconnect storm.
  React.useEffect(() => {
    const onFocus = () => {
      if (document.hidden) return;
      // Delay until after wsReconnectCoordinator's settle + first staggered resume
      // so HTTP fetches don't compete with WS handshakes on the same tick.
      setTimeout(() => {
        import('./stores/dataspaceContextStore').then(({ useDataspaceContextStore }) => {
          const store = useDataspaceContextStore.getState();
          const FIVE_MINUTES = 5 * 60 * 1000;
          if (!store.lastOrgLoadAt || Date.now() - store.lastOrgLoadAt > FIVE_MINUTES) {
            store.loadUserOrgs();
          }
          // refreshContext detects cross-device context changes and calls
          // applyRemoteContextChange if the server value differs from local state.
          store.refreshContext();
        }).catch(() => {});
      }, 1000);
    };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, []);

  // Initialize SystemStore globally (manages spaces and dataspace context).
  // Runs once per authenticated user — route navigation must NOT re-trigger it.
  React.useEffect(() => {
    // Cleanup handles for notification subscriptions created inside this effect.
    let unsubscribeNotifications: (() => void) | null = null;
    let unsubscribeNotificationsReconnect: (() => void) | null = null;

    const initSystemStore = async () => {
      perf.increment('systemStoreInitCount');

      // Skip on admin routes — they don't need entity/context bootstrap.
      if (location.pathname.startsWith('/admin')) {
        log.info('[SYSTEM_STORE_INIT] ⏭️ Skipping init for admin route');
        return;
      }

      const authToken = localStorage.getItem('authToken');
      if (!authToken || !user) return;

      // Once-guard: don't repeat org-load + context-fetch on navigation.
      if (systemStoreInitialized.current) {
        log.info('[SYSTEM_STORE_INIT] Already initialized — skipping repeat run');
        return;
      }
      systemStoreInitialized.current = true;

      perf.increment('systemStoreInitWithUser');
      try {
        const [
          { default: useSystemStore },
          { default: fetchService },
          { API_BASE_URL },
        ] = await Promise.all([
          import('./stores/systemStore'),
          import('./services/fetchService'),
          import('./api/config'),
        ]);

        // Kick off notifications Y.js in parallel with the network round-trips —
        // it only needs user.sub, which is already available from Auth0.
        const earlyUserId = user.sub ?? '';
        if (earlyUserId) {
          import('./stores/notificationsYjsStore').then(({ initNotificationsYjsStore }) => {
            initNotificationsYjsStore(earlyUserId);
          }).catch(() => {});
        }

        // Load orgs and fetch authoritative context in parallel — they are independent.
        const [, backendContextResult] = await Promise.allSettled([
          useSystemStore.getState().actions.loadUserOrgs(),
          fetchService(`${API_BASE_URL}/user/context`, { method: 'GET' }),
        ]);

        log.info('[SYSTEM_STORE_INIT] ✅ Organizations loaded');

        // Sync authoritative context into both stores atomically.
        try {
          if (backendContextResult.status === 'rejected') throw backendContextResult.reason;
          const backendContext = backendContextResult.value;

          log.info('[SYSTEM_STORE_INIT] 📥 Fetched context from backend:', {
            type: backendContext.type,
            orgId: backendContext.org_id,
            orgName: backendContext.org_name
          });

          const normalizedContext = {
            type: backendContext.type,
            orgId: backendContext.org_id || null,
            orgName: backendContext.org_name || null
          };

          // Write both stores in the same tick so they are never diverged.
          const { useDataspaceContextStore: dsStore } = await import('./stores/dataspaceContextStore');
          useSystemStore.setState({ currentContext: normalizedContext });
          dsStore.setState({ currentContext: normalizedContext });
          log.info('[SYSTEM_STORE_INIT] ✅ Both context stores synced from backend');
        } catch (contextError) {
          log.warn('[SYSTEM_STORE_INIT] Failed to fetch context from backend, using cached:', contextError);
        }

        log.info('[SYSTEM_STORE_INIT] ✅ System store initialized');

        // Align globalEntityStore dataspace with the authoritative context.
        // Org switches are also handled by globalEntityStore's own
        // dataspaceContextStore subscription, so this is a belt-and-braces
        // synchronization for the very first boot.
        const context = useSystemStore.getState().currentContext;
        const expectedDataspaceId = context.orgId ?? null;
        const { useGlobalEntityStore } = await import('./stores/globalEntityStore');
        const currentDataspaceId = useGlobalEntityStore.getState().dataspaceId;

        if (currentDataspaceId !== null && currentDataspaceId !== expectedDataspaceId) {
          log.warn(`[DATASPACE_INIT] Mismatch: context=${expectedDataspaceId ?? 'personal'} store=${currentDataspaceId ?? 'personal'} — resetting`);
          await useGlobalEntityStore.getState().reset();
        }
      } catch (error) {
        log.error('[SYSTEM_STORE_INIT] Failed to initialize system store:', error);
      }

      // Boot path for the new entity system:
      //   1. Read context (orgId or null).
      //   2. Start the org Y.Doc room (live-update bus).
      //   3. Initialize globalEntityStore (Space + Integration* + MCPConnection)
      //      via 3 parallel typed REST calls.
      //   4. Wire notifications.
      // Per-space entity loading is owned by SpacePageWrapper; never bulk-loaded.
      const { useGlobalEntityStore } = await import('./stores/globalEntityStore');

      if (useGlobalEntityStore.getState().lifecycle === 'uninitialized') {
        try {
          const useSystemStore = (await import('./stores/systemStore')).default;
          const context = useSystemStore.getState().currentContext;

          // Start entity-system Y.Doc before kicking off the global store init so
          // live updates from the org Y.Doc room are queued by the time the
          // initial REST burst returns.
          const dataspaceId = context.orgId || (user.sub ?? '');
          const { initEntitySystemYjsStore } = await import('./stores/entitySystemYjsStore');
          if (dataspaceId) initEntitySystemYjsStore(dataspaceId);
          log.info('[ENTITY_BOOT] Kicked off entity-system Y.Doc for dataspaceId:', dataspaceId);

          // Subscribe to real-time notifications via Y.js WebSocket room.
          const userId = user.sub ?? '';
          if (userId) {
            const {
              initNotificationsYjsStore,
              subscribeNotificationsYjs,
              subscribeNotificationsReconnect,
            } = await import('./stores/notificationsYjsStore');
            const { notificationService } = await import('./services/notificationService');

            initNotificationsYjsStore(userId);

            // Resync from MongoDB whenever the notification WS reconnects.
            // The server clears its events Y.Array after each push, so any
            // notification pushed while the client was disconnected is lost
            // from the CRDT — MongoDB has the authoritative copy.
            unsubscribeNotificationsReconnect = subscribeNotificationsReconnect(() => {
              notificationService.loadFromBackend().catch(() => {});
            });

            // Capture the unsubscribe so the cleanup function can tear it down.
            unsubscribeNotifications = subscribeNotificationsYjs((id, action, entry) => {
              if (!entry) return;

              if ((entry as any).type === 'job_status') {
                const ev = entry as any;
                log.info(
                  `[App] job_status via notification channel: job=${ev.jobId} ` +
                  `status=${ev.status} cancelRequested=${ev.cancelRequested}`
                );
                import('./apps/AutomationsApp/store/automationsStore').then(m => {
                  const stores = m.getAllAutomationsStores();
                  stores.forEach(store => {
                    store.getState().applyJobStatusEvent({
                      jobId: ev.jobId,
                      status: ev.status,
                      cancelRequested: ev.cancelRequested,
                      progress: ev.progress,
                    });
                  });
                });
                return;
              }

              if ((entry as any).type === 'toast') {
                notificationService.showToastFromEntry(entry as any);
                const threadId = (entry as any).data?.threadId;
                const status = (entry as any).priority === 'high' ? 'failed' : 'succeeded';
                if (threadId) {
                  window.dispatchEvent(
                    new CustomEvent('thread:agentRunStatus', { detail: { threadId, status } })
                  );
                }
                return;
              }

              if ((entry as any).type === 'automation_message') {
                const ev = entry as any;
                log.info(
                  `[App] automation_message notification: msg=${ev.data?.messageId} ` +
                  `job=${ev.data?.jobId} space=${ev.data?.spaceId}`
                );
                const msgText = (ev.message || '').trim();
                const previewTitle = ev.title
                  || (msgText.length > 50 ? msgText.slice(0, 50).trimEnd() + '…' : msgText || 'Agent message');
                notificationService.upsert({
                  id,
                  type: 'automation_message',
                  title: previewTitle,
                  message: ev.message || '',
                  status: 'pending',
                  read: false,
                  timestamp: ev.sentAt ? new Date(ev.sentAt).getTime() : Date.now(),
                  jobId: ev.data?.jobId,
                  messageId: ev.data?.messageId,
                  threadId: ev.data?.threadId,
                  automationName: ev.data?.automationName,
                  automationId: ev.data?.automationId,
                  spaceName: ev.data?.spaceName,
                  spaceId: ev.data?.spaceId,
                  actionUrl: ev.data?.spaceId
                    ? `/spaces/${ev.data.spaceId}`
                    : undefined,
                });
                return;
              }

              if ((entry as any).type === 'agent_message') {
                const ev = entry as any;
                log.info(`[App] agent_message notification: space=${ev.data?.spaceId}`);
                const msgText = (ev.message || '').trim();
                const previewTitle = ev.title
                  || (msgText.length > 50 ? msgText.slice(0, 50).trimEnd() + '…' : msgText || 'Notification');
                notificationService.upsert({
                  id,
                  type: 'agent_message',
                  title: previewTitle,
                  message: ev.message || '',
                  status: 'succeeded',
                  read: false,
                  timestamp: ev.sentAt ? new Date(ev.sentAt).getTime() : Date.now(),
                  automationName: ev.data?.automationName,
                  automationId: ev.data?.automationId,
                  spaceName: ev.data?.spaceName,
                  spaceId: ev.data?.spaceId,
                  actionUrl: ev.data?.spaceId
                    ? `/spaces/${ev.data.spaceId}`
                    : undefined,
                });
                return;
              }

              // Remote notification mutation (from AI tool or another device).
              // Update in-memory state without re-persisting to MongoDB.
              if ((entry as any).type === 'notification_update') {
                const ev = entry as any;
                if (ev.action === 'delete') {
                  notificationService.removeLocal(ev.notification_id);
                } else if (ev.action === 'mark_all_read') {
                  notificationService.markAllReadLocal();
                } else if (ev.action === 'mark_read') {
                  notificationService.markReadLocal(ev.notification_id);
                } else if (ev.action === 'mark_unread') {
                  notificationService.markUnreadLocal(ev.notification_id);
                } else if (ev.action === 'status_change') {
                  notificationService.updateStatusLocal(ev.notification_id, ev.data?.status, ev.data?.reply);
                }
                return;
              }

              // Another device switched teams — sync all stores immediately so this
              // device's UI and API calls stay consistent with the server-side context.
              if ((entry as any).type === 'context_changed') {
                const ev = entry as any;
                import('./stores/dataspaceContextStore').then(({ useDataspaceContextStore: dsStore }) => {
                  dsStore.getState().applyRemoteContextChange(ev.org_id, ev.org_name ?? null);
                }).catch(err => {
                  log.warn('[App] context_changed: failed to apply remote context change', err);
                });
                return;
              }

              const e = entry as any;
              const isFailed = e.priority === 'high' || e.priority === 'urgent'
                || e.message?.toLowerCase().includes('fail');

              notificationService.upsert({
                id,
                type: isFailed ? 'automation_error' : 'automation_complete',
                title: isFailed ? 'Automation failed' : 'Automation complete',
                message: e.message || '',
                status: isFailed ? 'failed' : 'succeeded',
                read: e.read === true,
                timestamp: e.sentAt ? new Date(e.sentAt).getTime() : Date.now(),
                jobId: e.data?.jobId,
                spaceId: e.data?.spaceId,
                // Prefer a structured /spaces/:id?thread=:id URL so ToastStack
                // can navigate properly. Fall back to legacy actionUrl if present.
                actionUrl: e.actionUrl ?? (
                  e.data?.spaceId && e.data?.threadId
                    ? `/spaces/${e.data.spaceId}?thread=${e.data.threadId}`
                    : e.data?.threadId
                    ? `/agents/${e.data.threadId}`
                    : undefined
                ),
              });

              log.debug('[NOTIFICATIONS] Live notification → popup', id, e.priority);
              notificationService.showToastFromEntry(e);
              const threadId = e.data?.threadId;
              if (threadId) {
                window.dispatchEvent(new CustomEvent('thread:agentRunStatus', {
                  detail: { threadId, status: isFailed ? 'failed' : 'succeeded' },
                }));
              }
            });

            // Seed in-memory store with persisted notifications so the bell and
            // NotificationsApp show correct data immediately after a page reload.
            notificationService.loadFromBackend().catch(() => {});

            log.info('[NOTIFICATIONS_INIT] Kicked off notifications Y.js for userId:', userId.slice(-6));
          }

          // Boot the global entity slice: Space + Integration* + MCPConnection.
          // ~3 typed REST calls in parallel (~50 entities total). Per-space entity
          // loading is owned by SpacePageWrapper.initializeSpace; never bulk-loaded
          // anywhere. The dataspace key is null for the personal context.
          await useGlobalEntityStore.getState().initialize(context.orgId ?? null);
          log.info('[ENTITY_BOOT] ✅ globalEntityStore ready for dataspaceId:', context.orgId ?? 'personal');
        } catch (error) {
          log.error('[ENTITY_BOOT] Failed:', error);
        }
      }
    };

    initSystemStore();

    return () => {
      if (unsubscribeNotifications) {
        unsubscribeNotifications();
        unsubscribeNotifications = null;
      }
      if (unsubscribeNotificationsReconnect) {
        unsubscribeNotificationsReconnect();
        unsubscribeNotificationsReconnect = null;
      }
    };
  // location.pathname intentionally excluded: this effect must not re-run on navigation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Parallel non-blocking init: local registries always; auth-gated registries only when token exists.
  React.useEffect(() => {
    const authToken = localStorage.getItem('authToken');

    // Local registries (entity types, tools, commands) need no auth.
    initializeLocalRegistries().catch(() => {});

    if (!authToken) return;

    Promise.all([
      import('./stores/appRegistryStore').then(({ useAppRegistryStore }) =>
        useAppRegistryStore.getState().initializeApps()
      ).catch(() => {}),

      initializeAuthRegistries(),

      import('./stores/userStore').then(({ default: s }) =>
        s.getState().fetchAdminStatus()
      ).catch(() => {}),
    ]).catch(() => {});
  }, []);

  // Register MCP tools for any connected servers once the global entity store
  // is ready. Subscribes to its lifecycle so we don't fire before
  // MCPConnection entities are loaded.
  React.useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return;

    const runMCPInit = async () => {
      try {
        const { useGlobalEntityStore } = await import('./stores/globalEntityStore');
        const { mcpAdapter } = await import('./services/mcp/mcpAdapter');
        const { mcpToolRegistry } = await import('./services/mcp/MCPToolRegistry');
        const { registerSelfContainedCommands } = await import('./stores/commandRegistryStore');
        const { entityService } = await import('./services/entityService');

        // MCPConnection is a global type — read from globalEntityStore.
        const mcpConnections = useGlobalEntityStore.getState().getByType('MCPConnection');
        const connected = mcpConnections.filter(
          (e) =>
            (e as any).skeleton?.status === 'connected' &&
            (e as any).skeleton?.manifest
        );

        if (connected.length === 0) {
          // Only count as a bug if entities weren't loaded yet. If lifecycle is
          // 'ready' and connected is empty, the user simply has no connected
          // MCP servers — fine.
          if (useGlobalEntityStore.getState().lifecycle !== 'ready') {
            perf.increment('mcpInitSkippedEmptyEntities');
          }
          return;
        }
        perf.increment('mcpInitRan');
        log.info(`[MCP_INIT] Checking ${connected.length} connected MCP servers`);

        for (const entity of connected) {
          const entityId = entity._id || (entity as any).id;
          try {
            const status = await mcpAdapter.getStatus(entityId);
            if (status.isActive) {
              const commands = mcpToolRegistry.registerConnection(
                entityId,
                entity.name,
                (entity as any).skeleton.manifest,
                (entity as any).skeleton.settings
              );
              registerSelfContainedCommands(commands);
              perf.increment('mcpToolsRegistered', commands.length);
              log.info(`[MCP_INIT] ✅ ${entity.name}: ${commands.length} tools registered`);
            } else {
              log.info(`[MCP_INIT] ${entity.name}: session gone, marking disconnected`);
              entityService.update(entityId, {
                'skeleton.status': 'disconnected',
              } as any).catch(err => log.warn(`[MCP_INIT] mark disconnected failed: ${err}`));
              if ((entity as any).skeleton?.settings?.autoReconnect) {
                log.info(`[MCP_INIT] ${entity.name}: autoReconnect triggered`);
                mcpAdapter.connect(entityId).then(({ manifest }) => {
                  if (manifest) {
                    const commands = mcpToolRegistry.registerConnection(
                      entityId, entity.name, manifest, (entity as any).skeleton.settings
                    );
                    registerSelfContainedCommands(commands);
                  }
                }).catch((err) => {
                  log.warn(`[MCP_INIT] autoReconnect failed for ${entity.name}:`, err);
                });
              }
            }
          } catch (err) {
            log.warn(`[MCP_INIT] failed to check ${entity.name}:`, err);
          }
        }
      } catch (err) {
        log.warn('[MCP_INIT] initialization error (non-fatal):', err);
      }
    };

    // Subscribe to globalEntityStore lifecycle; run MCP init once it's 'ready'
    // (i.e. the initial REST burst including MCPConnection has resolved).
    let ran = false;
    import('./stores/globalEntityStore').then(({ useGlobalEntityStore }) => {
      // Fast path: already ready when this effect runs (e.g. HMR reload).
      if (useGlobalEntityStore.getState().lifecycle === 'ready') {
        ran = true;
        runMCPInit();
        return;
      }
      const unsub = useGlobalEntityStore.subscribe((state) => {
        if (state.lifecycle === 'ready' && !ran) {
          ran = true;
          unsub();
          runMCPInit();
        }
      });
    }).catch(() => {});
  }, []);
  
  // Admin status now initialized in the parallel init effect above.

  // Handle URL-based deep linking to settings (works from ANY page including landing)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const settingsParam = urlParams.get('settings');
    const sectionParam = urlParams.get('section');
    
    if (settingsParam) {
      log.log('[App] 🔗 Detected settings URL deep link:', { tab: settingsParam, section: sectionParam });
      
      // Set state to open modal (works even on landing page without SystemMenuButton)
      setSettingsInitialTab(settingsParam);
      setSettingsInitialSection(sectionParam || undefined);
      setSettingsModalOpen(true);
      
      // Also dispatch event for pages that DO have SystemMenuButton
      window.dispatchEvent(new CustomEvent('openSettings', {
        detail: {
          tab: settingsParam,
          sectionId: sectionParam
        }
      }));
      
      // Clean up URL params
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('settings');
      newUrl.searchParams.delete('section');
      window.history.replaceState({}, '', newUrl);
      
      log.log('[App] ✅ Settings modal opened via URL deep link, URL cleaned');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  // Initialize navigation prevention to protect against accidental navigation
  React.useEffect(() => {
    enableNavigationPrevention({
      enableGestureBlocking: true,
      enableBeforeUnloadWarning: true,
      enableKeyboardShortcuts: true,
      customWarningMessage: 'You have unsaved changes in your documents. Are you sure you want to leave?'
    });

    log.log('[App] Navigation prevention enabled');
  }, []);

  // Registries now initialized in the parallel init effect above.

  // Analytics init: runs once on mount. Registers all provider adapters behind
  // the analytics facade, then initialises each one. The Mixpanel session-recording
  // poll is kept to re-identify the user once Mixpanel becomes ready.
  useEffect(() => {
    perf.increment('analyticsInitCount');
    const log = createFileLogger('APP_INIT');

    // Register providers once. analytics.init() below will call init() on each.
    analytics.register(new LogProvider());
    analytics.register(new MixpanelProvider());
    analytics.register(new GA4Provider());
    analytics.register(new PostHogProvider());
    analytics.register(new ServerRelayProvider());

    // MixpanelProvider.init() awaits the IDB sanitize promise from index.tsx;
    // no pre-flight needed here.
    (async () => {
      try {
        await analytics.init({});
        log.debug('Analytics facade initialized');
      } catch (error) {
        log.error('Failed to initialize analytics facade:', error);
      }
    })();

    // PostHog is also initialised directly here (posthog-js must be inited once
    // before the PostHogProvider can use it).
    (async () => {
      try {
        const { configService } = await import('./services/configService');
        const token = await configService.getPostHogProjectToken();
        const host = await configService.getPostHogHost();
        // Must align with analytics facade: posthog-js autocapture runs outside the facade.
        if (token && isTrackingEnabled()) {
          posthog.init(token, {
            api_host: host,
            defaults: '2026-01-30',
            person_profiles: 'identified_only',
            disable_session_recording: true,
            // Explicit session_recording config prevents rrweb from defaulting to
            // cross-origin iframe access, which throws SecurityError on Stripe Elements
            // and hCaptcha iframes. block_selector prevents rrweb from attempting to
            // snapshot iframe content that is cross-origin.
            session_recording: {
              recordCrossOriginIframes: false,
              maskAllInputs: true,
              maskInputOptions: { password: true },
              blockSelector: 'iframe[src*="js.stripe.com"], iframe[src*="stripe.network"], iframe[src*="hcaptcha.com"]',
            },
          });
          startPostHogRecordingForAuthenticatedUser();
          log.debug('PostHog initialized successfully');
        } else if (token) {
          log.debug('PostHog skipped (localhost / private host — use ?debug_analytics=1 to enable)');
        }
      } catch (error) {
        log.error('Failed to initialize PostHog:', error);
      }
    })();

    // Poll until Mixpanel is ready, then re-identify the user for session recording.
    // userTrackingService.init() was already called once in the mount effect above;
    // we only call it here on the timeout path as a safety net.
    let attempts = 0;
    const maxAttempts = 12;

    perf.increment('mixpanelPollStartCount');
    const pollForMixpanel = setInterval(() => {
      perf.increment('mixpanelPollFireCount');
      attempts++;

      if (mixpanelService.getInitializationStatus()) {
        perf.increment('mixpanelPollClearedCount');
        clearInterval(pollForMixpanel);
        log.debug('Mixpanel ready, re-identifying user for session recording');

        const currentUserId = userTrackingService.getCurrentUserId();
        const userType = userTrackingService.getUserType();

        if (currentUserId && currentUserId !== 'unknown' && userType === 'authenticated') {
          mixpanelService.identify(currentUserId);
          mixpanelService.ensureRecordingActive();
          ga4Service.identify(currentUserId);
          log.info('✅ Re-identified authenticated user and activated recording:', currentUserId);
        } else if (userType === 'anonymous') {
          log.debug('⏭️ Skipping recording for anonymous user (will activate after login/signup)');
        }

      } else if (attempts >= maxAttempts) {
        perf.increment('mixpanelPollClearedCount');
        clearInterval(pollForMixpanel);
        log.warn('Mixpanel initialization timeout, proceeding without analytics');
        // Safety net: ensure tracking is initialized even if Mixpanel never loaded.
        // Intentionally not incrementing userTrackingInitCount — this is a fallback, not a
        // second initialization. userTrackingService.init() is idempotent so calling it twice
        // is harmless, but counting it would falsely trigger the threshold violation.
        userTrackingService.init();
      }
    }, 5000);

    devOnly(() => {
      import('./utils/consoleDebugCommands').then(({ registerConsoleDebugCommands }) => {
        registerConsoleDebugCommands();
      }).catch(() => {});

      import('./utils/debugDataspace').then(() => {
        console.log('🔍 Dataspace debugging utilities loaded!');
        console.log('   Run: debugDataspace() - Full diagnostic report');
        console.log('   Run: clearEntityCache() - Clear all entity caches');
        console.log('   Run: reloadEntities() - Force reload entities from backend');
        console.log('   Run: switchToOrg("orgId") - Switch to org dataspace');
      }).catch(() => {});
    });

    import('./utils/lazyImports').then(({ preloadCommonModules }) => {
      preloadCommonModules();
    }).catch(() => {});

    return () => {
      clearInterval(pollForMixpanel);
      mixpanelService.stopRecording();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeProvider>
      <SystemContextProvider>
        <SelfHostedLicenseGate>
          <div className="app-container">
            <Suspense fallback={<Loader message="Loading River..." />}>
            <ScrollToTop />
            <NoIndexByRoute />
            <Routes>
            {/* ==== MAIN APPLICATION ROUTES ==== */}
            <Route
              path="/spaces/:id"
              element={
                <ProtectedRoute>
                  <SpacePageWrapperWithKey />
                </ProtectedRoute>
              }
            />
            <Route
              path="/new-space"
              element={
                <ProtectedRoute>
                  <Suspense fallback={null}>
                    <SpaceCreationPage
                      onClose={() => {
                        const riverOS = (window as any).riverOS;
                        if (riverOS?.window?.close) {
                          riverOS.window.close();
                        } else {
                          window.history.back();
                        }
                      }}
                    />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/new"
              element={
                <ProtectedRoute>
                  <Suspense fallback={null}>
                    <NewSpaceRoute />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <HomeSpaceWrapper />
                </ProtectedRoute>
              }
            />

            {/* ==== STANDALONE APP ROUTES ==== */}
            <Route
              path="/app/agent-panel"
              element={
                <AgentLayout>
                  <AgentPanelRoot />
                </AgentLayout>
              }
            />
            <Route
              path="/app/vault"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Vault">
                      <PasswordVaultApp windowId="standalone-vault" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/llmchef"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="LLM Chef">
                      <LLMChefApp windowId="standalone-llmchef" spaceId="default" onClose={() => {}} title="LLM Chef" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/listen"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Listen">
                      <ListenApp windowId="standalone-listen" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/mediagen"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Media Gen">
                      <MediaGenApp windowId="standalone-mediagen" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/promptlibrary"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Prompt Library">
                      <PromptLibraryApp windowId="standalone-promptlibrary" spaceId="default" onClose={() => {}} title="Prompt Library" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/inbox"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Inbox">
                      <InboxApp windowId="standalone-inbox" spaceId="default" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/quicklauncher"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Quick Launcher">
                      <QuickLauncherApp windowId="standalone-quicklauncher" spaceId="default" onClose={() => {}} title="Quick Launcher" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/store"
              element={
                <AdminProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Store">
                      <StoreApp windowId="standalone-store" spaceId="default" onClose={() => {}} title="Store" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/docs/:docId?"
              element={
                <GUILayout pageType="standalone">
                  <StandaloneAppLayout appTitle="River">
                    <DocumentAccessWrapper windowId="standalone-docs" spaceId="default" onClose={() => {}} title="Docs" />
                  </StandaloneAppLayout>
                </GUILayout>
              }
            />
            {/* Canonical shareable entity route — auth optional (public entities work without login) */}
            <Route
              path="/entity/:entityId"
              element={<StandaloneEntityView />}
            />

            {/* Public Automations Store catalog — must be before /automations/:entityId */}
            <Route
              path="/automations/store"
              element={
                <Suspense fallback={null}>
                  <AutomationsStorePage />
                </Suspense>
              }
            />

            {/* Type-specific alias: /automations/:id → StandaloneEntityView */}
            <Route
              path="/automations/:entityId"
              element={<StandaloneEntityView />}
            />

            {/* Type-specific alias: /sheets/:id → StandaloneEntityView */}
            <Route
              path="/sheets/:entityId"
              element={<StandaloneEntityView />}
            />

            {/* Type-specific aliases generated by entityLinkService capabilities */}
            <Route path="/notes/:entityId" element={<StandaloneEntityView />} />
            <Route path="/pages/:entityId" element={<StandaloneEntityView />} />
            <Route path="/canvas/:entityId" element={<StandaloneEntityView />} />
            <Route path="/slides/:entityId" element={<StandaloneEntityView />} />
            <Route path="/videos/:entityId" element={<StandaloneEntityView />} />
            <Route path="/images/:entityId" element={<StandaloneEntityView />} />
            <Route path="/dashboards/:entityId" element={<StandaloneEntityView />} />
            <Route path="/tasks/:entityId" element={<StandaloneEntityView />} />
            <Route path="/projects/:entityId" element={<StandaloneEntityView />} />
            <Route path="/contacts/:entityId" element={<StandaloneEntityView />} />
            <Route path="/tables/:entityId" element={<StandaloneEntityView />} />
            <Route path="/rules/:entityId" element={<StandaloneEntityView />} />
            <Route path="/web/:entityId" element={<StandaloneEntityView />} />
            <Route path="/files/:entityId" element={<StandaloneEntityView />} />
            <Route path="/folders/:entityId" element={<StandaloneEntityView />} />

            {/* Legacy /standalone/:id route — kept for backward compat, now points to new page */}
            <Route
              path="/standalone/:entityId"
              element={<StandaloneEntityView />}
            />
            <Route
              path="/app/chat"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Chat">
                      <ChatApp windowId="standalone-chat" spaceId="default" onClose={() => {}} title="Chat" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/entitybrowser2"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Entity Browser Pro">
                      <RiverDriveApp 
                        windowId="standalone-entitybrowser2" 
                        spaceId="default" 
                        onClose={() => {}} 
                        title="Entity Browser Pro" 
                      />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/banking"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Banking">
                      <BankApp 
                        onClose={() => {}} 
                        title="Banking"
                        windowId="standalone-banking-window" 
                        spaceId="standalone-space" 
                      />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/docs"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Documents">
                      <DocumentAccessWrapper windowId="standalone-docs" spaceId="default" onClose={() => {}} title="Documents" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/tasks"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Tasks" appId="tasks">
                      <TasksApp windowId="standalone-tasks" spaceId="default" onClose={() => {}} title="Tasks" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/automations"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Automations" appId="automations">
                      <AutomationsApp windowId="standalone-automations" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/contacts"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Contacts" appId="contacts">
                      <StandaloneContactsApp />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/app/mail"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Mail" appId="mail">
                      <StandaloneMailAppWrapper />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/messaging"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Messages" appId="messaging">
                      <StandaloneMessagingAppWrapper />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/bookkeeper"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Bookkeeper">
                      <BookkeeperApp windowId="bookkeeper-standalone" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/ingestwizard"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Ingest Wizard">
                      <IngestWizardApp windowId="standalone-ingestwizard" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/notes/:noteId?"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <ElectronAppLayout>
                      <NotesMarkdownApp windowId="standalone-notes" spaceId="default" onClose={() => {}} />
                    </ElectronAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route path="/app/login" element={<LoginApp />} />
            
            {/* ==== INTEGRATION CALLBACKS (Public - no auth required) ==== */}
            <Route path="/app/settings/integrations/google-drive/callback" element={<GoogleDriveCallbackPage />} />
            <Route path="/oauth/gmail/callback" element={<GmailOAuthCallback />} />
            <Route path="/plaid-oauth" element={<PlaidOAuthCallbackPage />} />
            
            {/* Blog subdomain Auth0 callback - centralized handler for all blog.rivereditor.com subdomains */}
            <Route path="/blog-auth-callback" element={<BlogAuthCallback />} />

            {/* ==== AUTH & LANDING ROUTES ==== */}
            <Route path="/electron-onboarding" element={<ElectronOnboardingPage />} />
            {/* Cinematic first-run onboarding for new Electron installs — Test at: http://localhost:3000/electron-first-run */}
            <Route path="/electron-first-run" element={<ElectronFirstRunPage />} />
            {/* Agents home — list and resume past agent runs. Hidden; kept here so we can re-enable later if needed. */}
            {/*
            <Route
              path="/agents"
              element={
                <ProtectedRoute>
                  <AgentsHome />
                </ProtectedRoute>
              }
            />
            */}
            {/* Agent conversation page — URL carries threadId so reloads restore exact session */}
            <Route
              path="/agents/:threadId"
              element={
                <ProtectedRoute>
                  <AgentConversationPage />
                </ProtectedRoute>
              }
            />
            {/* Dev-only: onboarding card UI playground — http://localhost:3000/first-run-preview */}
            {IS_DEV && <Route path="/first-run-preview" element={<OnboardingCardPreview />} />}
            {/* Dev-only: tool card state inspector — http://localhost:3000/tool-card-preview */}
            {IS_DEV && <Route path="/tool-card-preview" element={<ToolCardPreview />} />}
            {/* Dev-only: loader animation playground — http://localhost:3000/dev/loading */}
            {IS_DEV && <Route path="/dev/loading" element={<DevLoadingPage />} />}
            {/* Self-hosted onboarding with welcome animation - Test at: http://localhost:3000/selfhosted-onboarding */}
            <Route path="/selfhosted-onboarding" element={<SelfHostedOnboardingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/selfhosted-login" element={<SelfhostedLoginPage />} />
            {!([
              'bedrock.computer',
              'riversystem.ai',
            ].some(d => window.location.hostname.includes(d))) ? (
              <Route path="/signup" element={<SignupPage />} />
            ) : null}
            
            {/* Affiliate referral entry point — single link for all audiences */}
            <Route path="/r/:code" element={<AffiliateRefRoute />} />

            {/* Root route: authenticated users see QuickLauncher with chat, others see landing */}
            <Route path="/" element={<RootRoute />} />
            {/* Document route from launcher - shares same layout as root */}
            <Route path="/d/:docId" element={<RootRoute />} />
            
            {/* Public Book Viewer — accessible without an account */}
            <Route path="/book/:entityId" element={<PublicBookViewer />} />

            {/* Showcase Routes - Public */}
            <Route path="/showcase/:userId" element={<ShowcasePage />} />
            <Route path="/showcase/:userId/doc/:docId" element={<ShowcaseDocumentPage />} />
            {/* LEGACY: /onboarding/* paths - keeping for backwards compatibility */}
            <Route path="/onboarding/nsfw" element={<NSFWOnboarding />} />
            <Route path="/onboarding/blog-creator" element={<BlogCreatorOnboarding />} />
            <Route path="/onboarding/seo-blog-creator" element={<SEOBlogCreatorOnboarding />} />
            <Route path="/onboarding/seo-optimizer" element={<SEOOptimizerOnboarding />} />
            <Route path="/onboarding/content-polisher" element={<ContentPolisherOnboarding />} />
            <Route path="/onboarding/image-inserter" element={<ImageInserterOnboarding />} />
            <Route path="/onboarding/:persona" element={<TemplateOnboarding />} />
            
            {/* NEW: /tools/* paths - preferred going forward */}
            <Route path="/tools/nsfw" element={<CrossRuntimeRedirect to="/for/uncensored-ai-writer" />} />
            <Route path="/tools/blog-creator" element={<BlogCreatorOnboarding />} />
            <Route path="/tools/seo-optimizer" element={<SEOOptimizerOnboarding />} />
            <Route path="/tools/content-polisher" element={<ContentPolisherOnboarding />} />
            <Route path="/tools/image-inserter" element={<ImageInserterOnboarding />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/org-payment-success" element={<OrgPaymentSuccessPage />} />
            <Route path="/invitations/:token" element={<InvitationAcceptPage />} />
            {/* /downloads — ported to content-renderer. CloudFront routes the
                URL to CR; this redirect handles in-SPA <Link> clicks. */}
            <Route path="/downloads" element={<CrossRuntimeRedirect to="/downloads" />} />
            {/* /nsfw → /for/uncensored-ai-writer (canonical). CF handles the 301; this is defense-in-depth. */}
            <Route path="/nsfw" element={<CrossRuntimeRedirect to="/for/uncensored-ai-writer" />} />
            {/* /real-estate → /for/real-estate-developers via edge 301 (cloudfront-function-default-viewer-request.js) */}
            {/* /beta-read, /manuscript-feedback, /humanize-ai, /fact-checker — migrated to content-renderer /tools/* */}
            
            {/* Persona Pages - Organized under /personas/ for analytics bucketing */}
            <Route path="/personas/ai-grant-writer" element={<GrantWriters />} />
            {/* /for/uncensored-ai-writer/app is where the interactive workflow
                tool lives now that content-renderer serves /for/uncensored-ai-writer
                as the SSR marketing page. The marketing page CTAs deep-link to
                /app with ?signup=true so anonymous users sign up first.

                /personas/uncensored-ai-writer is kept rendering NSFWAIWriter as a
                safety net for users with the URL bookmarked or in flight, until
                the edge 301 to /for/uncensored-ai-writer takes over in CloudFront.
                Once GSC confirms the equity transfer (~2-6 weeks), this mount can
                go away. */}
            <Route path="/for/uncensored-ai-writer/app" element={<NSFWAIWriter />} />
            <Route path="/personas/uncensored-ai-writer" element={<NSFWAIWriter />} />

            {/* /for/ai-fanfiction-writer/app — interactive workflow for the
                fanfiction-writer audience. Marketing page is content-renderer
                SSR at /for/ai-fanfiction-writer; /personas/ai-fanfiction-writer
                stays mounted until the edge 301 picks up the equity transfer. */}
            <Route path="/for/ai-fanfiction-writer/app" element={<FanfictionWriter />} />
            {/* Phase 1.4 /for/* interactive workflows. Each marketing page is
                content-renderer SSR at /for/{slug}; the /app variant here
                hosts the interactive workflow component for signed-in users. */}
            <Route path="/for/grant-writer/app" element={<GrantWriters />} />
            <Route path="/for/rfp-writer/app" element={<RFPWriters />} />
            <Route path="/for/academic-writer/app" element={<AcademicWriters />} />
            <Route path="/for/family-office-writer/app" element={<HNWWriters />} />
            <Route path="/for/private-workspace/app" element={<PrivateWorkspace />} />
            <Route path="/for/creative-writing/app" element={<CreativeFreedom />} />
            {/* /personas/* SPA fallbacks removed — CF catch-all redirects /personas/* → /for/* */}
            <Route path="/addons/self-published-book" element={<SelfPublishedBookRedirect />} />
            <Route path="/addons/self-published-book/purchase" element={<BookAddonPurchasePage />} />
            {/* /personas/ai-real-estate-developer → /for/real-estate-developers via edge 301 */}

            {/* Dynamic Persona Pages migrated to content-renderer (/personas/[slug].tsx).
                Full-page navigation to /personas/<slug> is now served by CR via CloudFront. */}

            {/* /for/* and flat legacy URLs → canonical /for/<slug> (single-hop, no /personas/ intermediate).
                These use <CrossRuntimeRedirect> rather than <Navigate> because /for/<slug> is served
                by content-renderer, not the SPA — a client-side <Navigate> would fall through to
                the catch-all 404. CloudFront edge 301s catch these before the SPA loads in prod;
                this is the dev/stale-bundle safety net. See gui/src/components/CrossRuntimeRedirect.tsx. */}
            <Route path="/for/startups/fundraising" element={<CrossRuntimeRedirect to="/for/startup-founders" />} />
            <Route path="/for/startups/analytics" element={<CrossRuntimeRedirect to="/for/saas-operators" />} />
            <Route path="/for/startups/marketing" element={<CrossRuntimeRedirect to="/for/marketing-growth-teams" />} />
            <Route path="/for/startups/social-media" element={<CrossRuntimeRedirect to="/for/creators-influencers" />} />
            <Route path="/for/executives/strategy" element={<CrossRuntimeRedirect to="/for/ceos-executives" />} />
            <Route path="/for/executives/chief-of-staff" element={<CrossRuntimeRedirect to="/for/chiefs-of-staff" />} />
            <Route path="/for/executives/board-prep" element={<CrossRuntimeRedirect to="/for/board-investor-relations" />} />
            <Route path="/for/executives/communications" element={<CrossRuntimeRedirect to="/for/ceos-executives" />} />
            <Route path="/for/executives/weekly-briefings" element={<CrossRuntimeRedirect to="/for/chiefs-of-staff" />} />
            <Route path="/for/life/family-office" element={<CrossRuntimeRedirect to="/for/family-office-managers" />} />
            <Route path="/for/life/giving" element={<CrossRuntimeRedirect to="/for/philanthropy-teams" />} />
            <Route path="/for/life/estate-planning" element={<CrossRuntimeRedirect to="/for/estate-planning-teams" />} />
            <Route path="/for/life/concierge" element={<CrossRuntimeRedirect to="/for/busy-professionals" />} />
            <Route path="/for/life/personal-finance" element={<CrossRuntimeRedirect to="/for/busy-professionals" />} />

            {/* Legacy flat URLs → canonical /for/<slug> (cross-runtime — see above). */}
            <Route path="/grant-writing" element={<CrossRuntimeRedirect to="/for/grant-writer" />} />
            <Route path="/ai-grant-writer" element={<CrossRuntimeRedirect to="/for/grant-writer" />} />
            <Route path="/uncensored-ai-writer" element={<CrossRuntimeRedirect to="/for/uncensored-ai-writer" />} />
            <Route path="/private-ai-workspace" element={<CrossRuntimeRedirect to="/for/private-workspace" />} />
            <Route path="/private-workspace" element={<CrossRuntimeRedirect to="/for/private-workspace" />} />
            <Route path="/ai-creative-freedom" element={<CrossRuntimeRedirect to="/for/creative-writing" />} />
            <Route path="/creative-writing-ai" element={<CrossRuntimeRedirect to="/for/creative-writing" />} />
            <Route path="/fanfiction-ai-writer" element={<CrossRuntimeRedirect to="/for/ai-fanfiction-writer" />} />
            <Route path="/ai-fanfiction-writer" element={<CrossRuntimeRedirect to="/for/ai-fanfiction-writer" />} />
            
            {/* Tool Routes */}
            <Route path="/onboarding/humanizer" element={<HumanizerOnboarding />} />
            <Route path="/onboarding/welcome" element={<RiverOnboarding />} />
            
            {/* Space Templates Landing */}
            <Route path="/space-templates" element={<RiverSpacesLanding />} />

            {/* Product Routes */}
            <Route path="/products" element={<ProductsPage />} />
            {/* /river4, /personal-ai — moved to /x/ experimental namespace in content-renderer */}
            <Route path="/human-first" element={<HumanFirst />} />
            <Route path="/ai-personal-computer" element={<AiPersonalComputer />} />
            <Route path="/river-config" element={<RiverConfigFlow />} />
            <Route path="/work" element={<WorkLanding />} />
            <Route path="/chief-of-staff" element={<Navigate to="/work" replace />} />
            <Route path="/concierge" element={<ConciergeRiverLanding />} />
            <Route path="/twitter-agent" element={<TwitterAgentLanding />} />
            <Route path="/linkedin-agent" element={<LinkedInAgentLanding />} />
            <Route path="/linkedin-growth" element={<LinkedInGrowthLanding />} />
            <Route path="/reddit-agent" element={<RedditAgentLanding />} />
            <Route path="/seo-agent" element={<SeoAgentLanding />} />
            <Route path="/customer-success-agent" element={<CustomerSuccessLanding />} />
            <Route path="/auto-responder" element={<AutoResponderLanding />} />
            <Route path="/customer-support-agent" element={<CustomerSupportAgentLanding />} />
            <Route path="/claim/:packId" element={<ClaimPack />} />
            {/* Private, invite-only landing pages prepared for specific prospects. */}
            <Route path="/clients/cm" element={<ChrisLanding />} />
            <Route path="/clients/studio" element={<StudioClient />} />
            <Route path="/clients/davenport-roadhouse" element={<DavenportRoadhouseClient />} />
            {/* Per-app landing pages — Apple-style, AI-agent-centric */}
            <Route path="/apps/mail" element={<MailLanding />} />
            <Route path="/apps/tasks" element={<TasksLanding />} />
            <Route path="/apps/drive" element={<DriveLanding />} />
            <Route path="/apps/automations" element={<AutomationsLanding />} />
            <Route path="/apps/image-studio" element={<ImageStudioLanding />} />
            <Route path="/apps/video-studio" element={<VideoStudioLanding />} />
            <Route path="/apps/transcript" element={<TranscriptLanding />} />
            {/* /workspace — ported to content-renderer. */}
            <Route path="/workspace" element={<CrossRuntimeRedirect to="/workspace" />} />
            <Route path="/tasks" element={<TasksAppPage />} />
            <Route path="/os-signup" element={<OSSignupFlow />} />
            <Route path="/configure-river-1" element={<ConfigureRiver1Page />} />
            <Route path="/checkout" element={<RiverConfigFlow />} />
            <Route path="/checkout-old" element={<CheckoutPage />} />
            
            {/* Enterprise Routes — /enterprise/science and /academic-medicine-pi 301 at the edge to /solutions/life-sciences */}
            {/* /enterprise/consulting deleted — no audience, no traffic */}
            <Route path="/enterprise/law" element={<LawyerDocsEditor />} />

            {/* Use Case Routes */}
            <Route path="/use-cases/prelaw-deadline-panic" element={<PreLawDeadlinePanic />} />

            {/* Converter Routes */}
            <Route path="/convert/pdf-to-word" element={<PdfToWordConverter />} />
            <Route path="/manuscript-feedback/discover/:jobId" element={<ManuscriptDiscovery />} />
            <Route path="/manuscript-feedback/analyzing/:jobId" element={<ManuscriptAnalysisProgress />} />
            <Route path="/convert/chatgpt-to-word" element={<ChatGPTToWordConverter />} />
            <Route path="/convert/markdown-to-word" element={<MarkdownToWordConverter />} />

            {/* Tool Routes */}
            <Route path="/tools/youtube-script-creator" element={<YouTubeScriptCreator />} />
            <Route path="/tools/ai-product-description-generator" element={<AIProductDescriptionGenerator />} />
            <Route path="/tools/copyeditor" element={<CopyeditorLanding />} />
            <Route path="/tools/essay" element={<EssayReviewLanding />} />
            
            {/* Workflow Input Page - Must come BEFORE dynamic slug route */}
            <Route path="/tools/:slug/app" element={<WorkflowInputPage />} />
            
            {/* NEW: Workflow Landing Pages with dynamic slug */}
            <Route path="/tools/:slug" element={<WorkflowLanding />} />
            
            {/* SEO Blogs Landing Page - For the new blog creation system */}
            <Route path="/seo-blogs" element={<SEOBlogsLanding />} />
            
            {/* Blog Post Pages with dynamic slug */}
            <Route path="/blogs/:slug" element={<BlogLanding />} />
            
            {/* Guide Tool Pages - SPA routes for interactive tools */}
            <Route path="/guides/:slug/app" element={<WorkflowInputPage />} />
            
            {/* Getting Started Guide */}
            <Route path="/getting-started" element={<GettingStartedPage />} />
            
            {/* For [Audience] Pages */}
            <Route path="/for/ghostwriters" element={<GhostwriterLanding />} />
            <Route path="/for/grant-writers" element={<GrantWriterLanding />} />
            <Route path="/for/marketers" element={<MarketersLanding />} />
            {/* /for/researchers, /for/startups — migrated to content-renderer */}
            <Route path="/business-os" element={<BusinessOSLanding />} />
            {/* /automations — ported to content-renderer (was AgentsLanding). */}
            <Route path="/automations" element={<CrossRuntimeRedirect to="/automations" />} />
            <Route path="/automations/startups" element={<StartupAgentsLanding />} />
            <Route path="/for/students/:workflowSlug" element={<StudentWorkflowLanding />} />
            
            {/* Legacy redirects for backwards compatibility */}
            <Route path="/essay" element={<Navigate to="/tools/essay" replace />} />
            <Route path="/collegeapp" element={<CollegeEssayLanding />} />
            <Route path="/college-essay" element={<Navigate to="/tools/essay" replace />} />

            {/* Labs — internal experiments, noindex, served at /labs/* */}
            <Route path="/labs/avatar"  element={<AvatarPage />} />
            <Route path="/labs/avatar2" element={<AvatarPage2 />} />
            <Route path="/labs/avatar3" element={<AvatarPage3 />} />
            <Route path="/labs/riverbackgrounds" element={<RiverBackgrounds />} />

            {/* ==== ADMIN ROUTES ==== */}
            {/* Setup page — intentionally not behind AdminProtectedRoute so bootstrap works */}
            <Route
              path="/admin/setup"
              element={
                <GUILayout pageType="standalone">
                  <StandaloneAppLayout appTitle="Admin Setup">
                    <AdminSetup />
                  </StandaloneAppLayout>
                </GUILayout>
              }
            />
            {/* Legacy /admin redirects to setup (for bookmarks) */}
            <Route path="/admin" element={<Navigate to="/admin/setup" replace />} />
            {/* Legacy /admin-dashboard redirects to new overview */}
            <Route path="/admin-dashboard" element={<Navigate to="/admin/overview" replace />} />

            {/* Protected nested admin routes — all children share AdminLayout */}
            <Route
              path="/admin/*"
              element={
                <AdminProtectedRoute>
                  <GUILayout pageType="standalone">
                    <AdminLayout />
                  </GUILayout>
                </AdminProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/overview" replace />} />
              <Route path="overview" element={<AdminOverviewRoute />} />
              <Route path="overview/onboarding" element={<AdminOverviewOnboardingRoute />} />
              <Route path="overview/system" element={<AdminOverviewSystemRoute />} />
              <Route path="users" element={<AdminUsersRoute />} />
              <Route path="entities" element={<AdminEntitiesRoute />} />
              <Route path="entities/key-types" element={<AdminEntitiesKeyMetricsRoute />} />
              <Route path="entities/tools" element={<AdminEntitiesToolsRoute />} />
              <Route path="content" element={<AdminContentRoute />} />
              <Route path="growth" element={<AdminGrowthRoute />} />
              <Route path="activity" element={<AdminActivityRoute />} />
              <Route path="conversions" element={<AdminConversionsRoute />} />
              <Route path="signups-by-page" element={<AdminSignupsByPageRoute />} />
              <Route path="broadcast" element={<AdminBroadcastRoute />} />
              <Route path="analytics" element={<AdminAnalyticsOverviewRoute />} />
              <Route path="analytics/qau" element={<AdminAnalyticsQAURoute />} />
              <Route path="analytics/:metric" element={<AdminAnalyticsMetricRoute />} />
              <Route path="activation" element={<AdminActivationRoute />} />
              <Route path="llm-usage" element={<AdminLLMUsageRoute />} />
              <Route path="chat-input" element={<AdminChatInputRoute />} />
              <Route path="integrations" element={<AdminIntegrationsRoute />} />
            </Route>

            {/* ==== PUBLIC ROUTES (no auth required) ==== */}
            <Route
              path="/broadcast-unsubscribe"
              element={<BroadcastUnsubscribePageLazy />}
            />

            {/* ==== FALLBACK ROUTES ====
                Real 404. Previously this redirected to "/" with HTTP 200, which
                trained Google to treat every unknown URL as a homepage duplicate
                (soft-404). NotFound emits <meta name="robots" content="noindex">
                via Helmet so crawlers drop these URLs from the index. The HTTP
                status is still 200 because CloudFront's custom_error_response
                swaps S3 404s -> 200 + /index.html so SPA routing works; the
                noindex meta is what does the SEO work. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>

        {/* Global Upgrade Modal with Tier Selection */}
        <UpgradeModal />

        {/* Invite-a-Friend Modal (free users, 100-credit threshold) — disabled */}
        {/* {showInviteFriendModal && (
          <Suspense fallback={null}>
            <InviteFriendModalLazy onClose={() => setShowInviteFriendModal(false)} subtitle="Get free usage — share River and instantly unlock:" />
          </Suspense>
        )} */}

        {/* Global Tool Consent Modal */}
        <ToolConsentModal />

        {/* Toast containers for all positions actively used in the codebase */}
        <ToastContainer position="top-right" maxToasts={5} />
        <ToastContainer position="top-center" maxToasts={3} />
        <ToastContainer position="bottom-right" maxToasts={3} />
        <ToastContainer position="center" maxToasts={3} />

        <ToastStack />

        {/* Window controls for frameless Electron on Linux/Windows */}
        <WindowControls />

        {/* Auto-update toast notification */}
        <React.Suspense fallback={null}>
          <UpdateToast />
        </React.Suspense>
        {/* Rosetta warning — shown on Apple Silicon Macs running the x64 build */}
        <React.Suspense fallback={null}>
          <RosettaBanner />
        </React.Suspense>
        {/* Screen Recording permission prompt — shown when macOS TCC denies access */}
        <React.Suspense fallback={null}>
          <ScreenRecordingBanner />
        </React.Suspense>
        {/* Session Expired Modal */}
        <SessionExpiredModal
          isOpen={isSessionExpiredModalOpen}
          onLogin={handleLogin}
        />

        {/* RiverOS v1 Purchase Modal */}
        <OSSignupModal />
        
        {/* Org Payment Gate - Blocks unpaid orgs */}
        <OrgPaymentGateWrapper />
        
        {/* Global Settings Modal (for URL deep linking from landing pages) */}
        {settingsModalOpen && (
          <React.Suspense fallback={<div />}>
            <UserSettingsModal
              isOpen={settingsModalOpen}
              onClose={() => {
                setSettingsModalOpen(false);
                setSettingsInitialTab(undefined);
                setSettingsInitialSection(undefined);
              }}
              initialTab={settingsInitialTab as any}
              initialSectionId={settingsInitialSection}
            />
          </React.Suspense>
        )}
        
        {/* Global Contextual AI Modal — opened by Ambient Intelligence Surface */}
        {GlobalCmdKModal}

        {/* Global Entity Search — Cmd+K */}
        {GlobalEntitySearchModal}

        {/* Ambient Intelligence Surface — reactive suggestions triggered by world events */}
        {AmbientBubble}
        
        {/* Y.js Debug Panel — toggle with Ctrl+Shift+Y */}
        <React.Suspense fallback={null}>
          <YjsDebugPanel />
        </React.Suspense>

          </div>
        </SelfHostedLicenseGate>
      </SystemContextProvider>
    </ThemeProvider>
  );
}

export default App;
